import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { PersonFinal } from '@/api/entities';
import { AccountFinal } from '@/api/entities';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { notify } from '@/components/ui/notify';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  Mail, 
  Search, 
  Filter,
  Loader2,
  Calendar,
  Building2,
  CheckCircle2,
  Linkedin,
  Phone,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ChevronDown
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import OutboxDrawer from '../components/outbox/OutboxDrawer';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import { getConnectedEmailProviderTokens as resolveEmailProviderTokens } from '@/lib/emailProviderTokens';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { normalizePlaybookKey, createPageUrl } from '@/utils';
import { sanitizeId } from '@/components/utils/id';
import { getBestPersonProfileLinkId } from '@/lib/personProfileLinkId';
import { useTranslation } from '@/components/utils/translations';
import LinkedInSubtypeBadge from '@/components/common/LinkedInSubtypeBadge';
import {
  AD_HOC_SCHEDULED_SEQUENCE_ID,
  dedupeOutboxDraftsByStepId,
  formatOutboxDateLine,
  getOutboxCampaignKey,
  getOutboxScheduledSendTimestamp,
  getOutboxStatusBadge,
  getOutboxStatusSubtext,
  isAdHocScheduledOutboxDraft,
  isOutboxGeneratingStatus,
  matchesOutboxDateFilter,
  resolveOutboxDisplayStatus,
} from '@/lib/outboxStatusDisplay';
import {
  buildOutboxChannelRecordingPayload,
  enrichOutboxDraftsWithSequencePlans,
  getChannelDisplayLabel,
  getOutboxDraftHeadline,
  isCallOutboxChannel,
  isEmailOutboxChannel,
  isLinkedInOutboxChannel,
  logLinkedInChannelOutboxBatch,
  withLinkedInSequenceAnnotation,
} from '@/lib/outboxChannel';
import { personaFitBand, PERSONA_FIT_LABELS } from '@/lib/personaFitLabel';

const API_BASE_URL = window.location.hostname === 'localhost' 
  ? 'https://api.neogtm.com' 
  : 'https://api.neogtm.com';

const ITEMS_PER_PAGE = 500;
/** Max rows from GET /api/outbox/ad-hoc-history for per-persona last ad-hoc badges. */
const AD_HOC_SENT_HISTORY_LIMIT = 3000;
/** Max rows from GET /api/outbox/upcoming-scheduled (fallback: status=scheduled inbox query). */
const SCHEDULED_HISTORY_LIMIT = 3000;
const SCHEDULED_HISTORY_UNSUPPORTED_KEY = 'outbox-scheduled-history-404';

/** Keep header "Showing X of Y" in sync when rows are removed locally (delete / done / schedule). */
function decrementOutboxPagination(setPagination, removedCount, itemsPerPage, currentPage) {
  const n = Number(removedCount);
  if (!Number.isFinite(n) || n < 1) return;
  const page = Math.max(1, Number(currentPage) || 1);
  const ipp = Math.max(1, Number(itemsPerPage) || 1);
  setPagination((prev) => {
    const nextTotal = Math.max(0, (prev.total_count || 0) - n);
    const shownThroughPage = page * ipp;
    return {
      ...prev,
      total_count: nextTotal,
      total_pages: Math.max(1, Math.ceil(nextTotal / ipp)),
      has_more: nextTotal > shownThroughPage,
    };
  });
}

function resolveOutboxTotalCount(data, draftsArray) {
  const pag = data?.pagination;
  const candidates = [
    pag?.total_count,
    pag?.total,
    data?.filtered_total,
    data?.total_count,
    data?.total,
    data?.count,
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return Array.isArray(draftsArray) ? draftsArray.length : 0;
}

/** Forward ad-hoc LLM metadata to /api/outbox/:id/send when present on the draft. */
function pickAdHocGenerationMetaForSend(draft) {
  const extra = {};
  if (Array.isArray(draft?.used_signals)) extra.used_signals = draft.used_signals;
  if (typeof draft?.focus === 'string') extra.focus = draft.focus;
  if (draft?.context_signals && typeof draft.context_signals === 'object') {
    extra.context_signals = draft.context_signals;
  }
  if (draft?.style_sample_id != null && draft.style_sample_id !== '') {
    extra.style_sample_id = draft.style_sample_id;
  }
  return extra;
}

function getPersonProfileUrlFromDraft(draft) {
  const personId = sanitizeId(getBestPersonProfileLinkId(draft));
  if (!personId) return null;
  const accountId = sanitizeId(
    draft?.account_id || draft?.accountId || draft?.account?.id || draft?.account?.account_id
  );
  const path = accountId
    ? `PersonProfile?id=${encodeURIComponent(personId)}&accountId=${encodeURIComponent(accountId)}`
    : `PersonProfile?id=${encodeURIComponent(personId)}`;
  return createPageUrl(path);
}

function mergeLastAdHocPersonaMaps(primary, fallback) {
  const out = new Map(primary);
  fallback.forEach((v, k) => {
    const prev = out.get(k);
    if (!prev || v.dateObj > prev.dateObj) out.set(k, v);
  });
  return out;
}

function isLikelyAdHocOutboxSend(row) {
  // Rows from GET /api/outbox/ad-hoc-history are already scoped to ad-hoc sends.
  if (row?.is_history === true) {
    const status = String(row?.status || '').toLowerCase();
    return !status || status === 'sent' || status === 'manual_completion';
  }
  const status = String(row?.status || '').toLowerCase();
  if (status !== 'sent' && status !== 'manual_completion') return false;
  const stepId = String(row?.stepId || row?.id || '');
  const hasSequence = Boolean(row?.sequence_id || row?.sequenceId || row?.sequence?.id);
  const hasPlaybook = Boolean(row?.playbook_key || row?.playbook_id || row?.sequence?.playbook_key || row?.sequence?.playbook_id);
  const source = String(row?.source || row?.message_source || row?.origin || '').toLowerCase();
  return (
    row?.is_temporary === true ||
    stepId.startsWith('draft-') ||
    source.includes('ad_hoc') ||
    source.includes('adhoc') ||
    source.includes('manual') ||
    (!hasSequence && !hasPlaybook)
  );
}

/** Prefer actual send/completion timestamps — not draft created_at (often days earlier). */
function getSentAtFromOutboxRow(row) {
  const explicit =
    row?.last_sent_at ||
    row?.lastSentAt ||
    row?.sent_at ||
    row?.sentAt ||
    row?.completed_at ||
    row?.manual_completed_at ||
    row?.marked_done_at ||
    row?.updated_at ||
    row?.updatedAt;
  if (explicit) return explicit;
  const status = String(row?.status || '').toLowerCase();
  if (row?.is_history === true || status === 'sent' || status === 'manual_completion') {
    return null;
  }
  return row?.createdAt || row?.created_at;
}

function formatAdHocRelativeLabel(date) {
  const now = new Date();
  const sentDateLocal = date.toLocaleDateString('en-CA');
  const todayLocal = now.toLocaleDateString('en-CA');
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayLocal = yesterday.toLocaleDateString('en-CA');
  const sentDateObj = new Date(sentDateLocal);
  const todayDateObj = new Date(todayLocal);
  const diffDays = Math.round((todayDateObj - sentDateObj) / (1000 * 60 * 60 * 24));
  if (sentDateLocal === todayLocal) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  if (sentDateLocal === yesterdayLocal) return 'Yesterday';
  if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'short' });
  if (diffDays < 365) return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

function bumpLastAdHocPersonaMap(prevMap, row, formatRelativeLabelFn, sentAtOverride = null) {
  if (!isLikelyAdHocOutboxSend(row)) return prevMap;
  const sentDate =
    sentAtOverride instanceof Date && !Number.isNaN(sentAtOverride.getTime())
      ? sentAtOverride
      : (() => {
          const raw = getSentAtFromOutboxRow(row);
          if (!raw) return new Date();
          try {
            const d = typeof raw === 'string' ? parseISO(raw) : new Date(raw);
            return Number.isNaN(d.getTime()) ? new Date() : d;
          } catch {
            return new Date();
          }
        })();
  const channel = channelLabelFromOutboxRow(row);
  const fullDate = sentDate.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  const entry = {
    dateObj: sentDate,
    date: formatRelativeLabelFn(sentDate),
    fullDate,
    channel,
  };
  const next = new Map(prevMap);
  getPersonaIdsFromOutboxRow(row).forEach((id) => {
    const prev = next.get(id);
    if (!prev || sentDate > prev.dateObj) next.set(id, entry);
  });
  return next;
}

function channelLabelFromOutboxRow(row) {
  const channel =
    row?.last_sent_channel || row?.lastSentChannel || row?.channel;
  return getChannelDisplayLabel(
    isLinkedInOutboxChannel({ channel }) ? row : { channel },
  );
}

function getPersonaIdsFromOutboxRow(row) {
  return [
    row?.persona_id,
    row?.person_id,
    row?.personId,
    row?.person?.persona_id,
    row?.person?.person_id,
    row?.person?.id,
  ]
    .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
    .map((v) => String(v));
}

function buildLastAdHocPersonaMapFromRows(rows, toValidDateFn, formatRelativeLabelFn) {
  const map = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!isLikelyAdHocOutboxSend(row)) return;
    const sentAt = getSentAtFromOutboxRow(row);
    const sentDate = toValidDateFn(sentAt);
    if (!sentDate) return;
    const channel = channelLabelFromOutboxRow(row);
    const fullDate = sentDate.toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
    getPersonaIdsFromOutboxRow(row).forEach((id) => {
      const prev = map.get(id);
      if (!prev || sentDate > prev.dateObj) {
        map.set(id, {
          dateObj: sentDate,
          date: formatRelativeLabelFn(sentDate),
          fullDate,
          channel,
        });
      }
    });
  });
  return map;
}

// [2026-07-01] Builds a per-persona map of the MOST-RECENT playbook touch
// sent (sequence_id NOT NULL). Mirrors buildLastAdHocPersonaMapFromRows
// but for the /scheduled-history feed. Powers the "Last playbook touch
// sent: T{stage} {channel} · {date}" line under the Outbox draft cards.
// Fix for Zalizah Awang Long case — she had 5 delivered playbook touches
// but the Outbox row showed no recent activity indicator because only
// ad-hoc history was surfaced.
function buildLastPlaybookPersonaMapFromRows(rows, toValidDateFn, formatRelativeLabelFn) {
  const map = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    // Only playbook-driven sends here — anything with no sequence_id skips.
    if (!row?.sequence_id) return;
    const sentAt = row?.sent_at || row?.created_at || null;
    const sentDate = toValidDateFn(sentAt);
    if (!sentDate) return;
    const channel = String(row?.channel || 'email');
    const channelLabel = channel === 'email' ? 'Email'
      : channel.startsWith('linkedin') ? 'LinkedIn'
      : channel === 'call' ? 'Call'
      : channel;
    const stageNo = row?.stage_no ?? null;
    const fullDate = sentDate.toLocaleDateString([], {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
    const pid = row?.person_id;
    if (!pid) return;
    const prev = map.get(pid);
    if (!prev || sentDate > prev.dateObj) {
      map.set(pid, {
        dateObj: sentDate,
        date: formatRelativeLabelFn(sentDate),
        fullDate,
        channel: channelLabel,
        stage_no: stageNo,
      });
    }
  });
  return map;
}

function extractOutboxRowsFromResponse(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.drafts)) return data.drafts;
  if (data?.data && Array.isArray(data.data)) return data.data;
  return [];
}

function isScheduledOutboxRow(row) {
  const status = String(row?.status ?? row?.generation_status ?? '').toLowerCase();
  return status === 'scheduled';
}

function getScheduledAtFromOutboxRow(row) {
  return (
    row?.scheduled_send_at ??
    row?.scheduledSendAt ??
    row?.scheduled_at ??
    row?.scheduledAt ??
    row?.due_at ??
    row?.dueAt ??
    null
  );
}

function summarizeScheduledItems(items) {
  const sorted = [...items].sort((a, b) => a.dateObj - b.dateObj);
  return {
    count: sorted.length,
    next: sorted[0] || null,
    items: sorted,
  };
}

function buildScheduledEntryFromRow(row, toValidDateFn, formatScheduledLabelFn) {
  const at = toValidDateFn(getScheduledAtFromOutboxRow(row));
  if (!at) return null;
  return {
    stepId: String(row?.stepId || row?.id || ''),
    dateObj: at,
    date: formatScheduledLabelFn(at),
    fullDate: at.toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }),
    channel: channelLabelFromOutboxRow(row),
    subject: String(row?.subject || row?.message_preview || '').trim() || null,
  };
}

function buildScheduledPersonaMapFromRows(rows, toValidDateFn, formatScheduledLabelFn) {
  const byPersona = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!isScheduledOutboxRow(row)) return;
    const entry = buildScheduledEntryFromRow(row, toValidDateFn, formatScheduledLabelFn);
    if (!entry) return;
    getPersonaIdsFromOutboxRow(row).forEach((id) => {
      if (!byPersona.has(id)) byPersona.set(id, []);
      const list = byPersona.get(id);
      if (entry.stepId && list.some((x) => x.stepId === entry.stepId)) return;
      list.push(entry);
    });
  });
  const out = new Map();
  byPersona.forEach((items, id) => {
    out.set(id, summarizeScheduledItems(items));
  });
  return out;
}

function mergeScheduledPersonaMaps(primary, fallback) {
  const out = new Map(primary);
  fallback.forEach((bundle, personaId) => {
    const prev = out.get(personaId);
    if (!prev) {
      out.set(personaId, bundle);
      return;
    }
    const mergedItems = [...prev.items];
    bundle.items.forEach((item) => {
      if (item.stepId && mergedItems.some((x) => x.stepId === item.stepId)) return;
      mergedItems.push(item);
    });
    out.set(personaId, summarizeScheduledItems(mergedItems));
  });
  return out;
}

const normalizeOutboxPlaybookKey = (rawKey) => {
  const raw = String(rawKey || '').trim();
  if (!raw) return '';
  // Synthetic event custom keys must keep the full event::<uuid> value.
  if (raw.toLowerCase().startsWith('event::')) return raw;
  return normalizePlaybookKey(raw);
};

const getPlaybookOptionValue = (playbook) => {
  const eventId = playbook?.sequence_event_id || playbook?.event_id || '';
  return eventId ? `${playbook.key}|||${eventId}` : String(playbook.key || '');
};

const parsePlaybookOptionValue = (value) => {
  if (!value || value === 'all') return { key: '', sequenceEventId: '' };
  const raw = String(value);
  const separatorIndex = raw.indexOf('|||');
  if (separatorIndex < 0) return { key: raw, sequenceEventId: '' };
  return {
    key: raw.slice(0, separatorIndex) || '',
    sequenceEventId: raw.slice(separatorIndex + 3) || '',
  };
};

const isCustomPlaybookOption = (playbook) => {
  if (!playbook) return false;
  return Boolean(
    playbook.is_diy ||
    playbook.is_event_custom ||
    playbook.use_custom_playbook ||
    playbook.custom_playbook_activated ||
    playbook.custom_message_activated
  );
};

// Generate page numbers with ellipsis for large page counts
const generatePageNumbers = (current, total) => {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  
  const pages = [];
  pages.push(1);
  
  if (current > 3) pages.push('...');
  
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
    pages.push(i);
  }
  
  if (current < total - 2) pages.push('...');
  if (total > 1) pages.push(total);
  
  return pages;
};

const PERSONA_COLORS = {
  strategic: "bg-green-100 text-green-800",
  supportive: "bg-blue-100 text-blue-800",
  passive: "bg-gray-100 text-gray-800",
  lowFit: "bg-red-100 text-red-800",
};
const getPersonaLabel = (score) => {
  const band = personaFitBand(score) ?? 'passive';
  return { label: PERSONA_FIT_LABELS[band], color: PERSONA_COLORS[band] };
};

export default function Outbox({ currentTenant: currentTenantProp }) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const currentTenant = currentTenantProp || contextTenant;
  const tenantId = resolveEffectiveTenant(
    contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
    currentTenant,
    contextUser,
    'instructure'
  );
  const user = contextUser;
  const [drafts, setDrafts] = useState([]);
  const [people, setPeople] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [dailyTally, setDailyTally] = useState({ count: 0, threshold: 200, over_threshold: false });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(new Set(['draft_ready'])); // Default: drafts only (scheduled is opt-in)
  const [tierFilter, setTierFilter] = useState(new Set()); // Multi-select: Set of tiers, empty means all
  const [touchFilter, setTouchFilter] = useState(new Set()); // Set of selected touch numbers, empty means all
  const [channelFilter, setChannelFilter] = useState(new Set()); // Multi-select: Set of channels, empty means all
  const [countryFilter, setCountryFilter] = useState(new Set()); // Multi-select: Set of countries, empty means all
  const [sectorFilter, setSectorFilter] = useState(new Set()); // Multi-select: Set of sectors, empty = all
  // Min social_presence_score on persona. 'all' = unset.
  const [socialPresenceFilter, setSocialPresenceFilter] = useState('all');
  // Min persona_fit_score on persona. 'all' = unset.
  const [personaFitFilter, setPersonaFitFilter] = useState('all');
  const [archetypeFilter, setArchetypeFilter] = useState('all');
  const [eventFilter, setEventFilter] = useState('everyone'); // Single-select: 'everyone', event name, or 'no_events'
  const [dateFilter, setDateFilter] = useState('all');
  const [generatedFilter, setGeneratedFilter] = useState('all');
  const [hideRecentWorkdays, setHideRecentWorkdays] = useState('4'); // string so '' = off
  const [debouncedHideRecentWorkdays, setDebouncedHideRecentWorkdays] = useState('4');
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [selectedDraftIds, setSelectedDraftIds] = useState(new Set());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [resendBlocked, setResendBlocked] = useState(null);
  const [resendBlockedSecondsLeft, setResendBlockedSecondsLeft] = useState(0);
  const [isHandlingResendBlocked, setIsHandlingResendBlocked] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total_count: 0,
    total_pages: 1,
    has_more: false
  });
  const [playbookFilter, setPlaybookFilter] = useState('');
  const [sequenceEventFilterId, setSequenceEventFilterId] = useState('');
  const [availablePlaybooks, setAvailablePlaybooks] = useState([]);
  const [availableProductContexts, setAvailableProductContexts] = useState([]);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [availableSectors, setAvailableSectors] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [productContextFilter, setProductContextFilter] = useState('all');
  const [lastAdHocFromSentHistory, setLastAdHocFromSentHistory] = useState(() => new Map());
  const [lastPlaybookFromSentHistory, setLastPlaybookFromSentHistory] = useState(() => new Map());
  const [scheduledFromHistory, setScheduledFromHistory] = useState(() => new Map());
  const loadDataLatestRef = useRef(null);
  const outboxLoadRequestRef = useRef(0);
  const hasLoadedOutboxOnceRef = useRef(false);

  useEffect(() => {
    if (!resendBlocked || resendBlockedSecondsLeft <= 0) return;
    const id = setInterval(() => {
      setResendBlockedSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [resendBlocked, resendBlockedSecondsLeft]);

  const mergedAvailablePlaybooks = React.useMemo(() => {
    const merged = new Map();
    (availablePlaybooks || []).forEach((p) => {
      const key = normalizeOutboxPlaybookKey(p?.key || '');
      if (!key) return;
      const eventId = p?.sequence_event_id || p?.event_id || '';
      const compoundKey = `${key}::${eventId || ''}`;
      if (!merged.has(compoundKey)) {
        merged.set(compoundKey, {
          ...p,
          key,
          name: p?.name || p?.playbook_name || key,
          is_diy: Boolean(p?.is_diy),
          is_event_custom: Boolean(p?.is_event_custom),
          use_custom_playbook: Boolean(p?.use_custom_playbook),
          custom_playbook_activated: Boolean(p?.custom_playbook_activated),
          custom_message_activated: Boolean(p?.custom_message_activated),
        });
      }
    });
    return Array.from(merged.values());
  }, [availablePlaybooks]);

  // Extract available events from people data (similar to People page)
  const availableEvents = React.useMemo(() => {
    const events = new Set();
    people.forEach(person => {
      if (Array.isArray(person.events_attended) && person.events_attended.length > 0) {
        person.events_attended.forEach(event => {
          if (event) events.add(event);
        });
      }
    });
    return Array.from(events).sort();
  }, [people]);

  const availableArchetypes = React.useMemo(() => {
    const archetypes = new Set();
    people.forEach((person) => {
      const label = String(person?.archetype_name || person?.archetype || '').trim();
      if (label) archetypes.add(label);
    });
    return Array.from(archetypes).sort((a, b) => a.localeCompare(b));
  }, [people]);

  const personasById = React.useMemo(() => {
    const m = new Map();
    for (const p of people) {
      [p?.persona_id, p?.person_id, p?.id]
        .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
        .forEach((id) => m.set(String(id), p));
    }
    return m;
  }, [people]);

  // Handle draft query parameter to open a specific draft
  useEffect(() => {
    const draftId = searchParams.get('draft');
    const searchQuery = searchParams.get('search');
    
    if (draftId && drafts.length > 0) {
      const draft = drafts.find(d => (d.stepId || d.id) === draftId);
      if (draft && !selectedDraft) {
        setSelectedDraft(draft);
        // Remove the query parameter after opening
        searchParams.delete('draft');
        setSearchParams(searchParams, { replace: true });
      }
    }
    
    if (searchQuery && !searchTerm) {
      setSearchTerm(searchQuery);
      // Remove the query parameter after setting search
      searchParams.delete('search');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, drafts, selectedDraft, searchTerm]);

  // Load available playbooks for filter
  useEffect(() => {
    const loadPlaybooks = async () => {
      if (!contextUser?.id) return;
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/outbox/playbooks?tenant_id=${tenantId}&user_id=${encodeURIComponent(contextUser.id)}`,
          { credentials: 'include' }
        );
        if (response.ok) {
          const data = await response.json();
          const rows = Array.isArray(data?.playbooks) ? data.playbooks : Array.isArray(data) ? data : [];
          setAvailablePlaybooks(rows);
          const contexts = Array.isArray(data?.product_contexts) ? data.product_contexts : [];
          setAvailableProductContexts(
            [...new Set(contexts.map((c) => String(c || '').trim()).filter(Boolean))].sort()
          );
        }
      } catch (error) {
        console.error('Error loading playbooks:', error);
      }
    };
    loadPlaybooks();
  }, [currentTenant, contextUser, tenantId]);

  // Load accounts and available countries for filter
  useEffect(() => {
    const loadAccounts = async () => {
      if (!contextUser) return;
      try {
        // Load accounts
        const allAccounts = await AccountFinal.list();
        
        // Filter by tenant
        const tenantMatches = (record) => {
          if (!record) return false;
          if (tenantId === 'smowl') return record.tenant_id === 'smowl';
          if (record.tenant_id === tenantId || record.tenant_id === 'global') return true;
          if (tenantId === 'instructure' && (record.tenant_id === 'default' || !record.tenant_id)) return true;
          if (Array.isArray(record.visible_to_tenants) && record.visible_to_tenants.includes(tenantId)) return true;
          if (record.tenant_overrides && record.tenant_overrides[tenantId]) return true;
          if (tenantId === 'diambra' && (record.tenant_id === 'smowl' || record.tenant_id === 'instructure' || record.tenant_id === 'clairelabs' || record.tenant_id === 'base44' || record.tenant_id === 'default')) return true;
          return false;
        };

        const filteredAccounts = allAccounts
          .filter(a => a.is_visible !== false && tenantMatches(a))
          .map(acc => {
            if (acc.tenant_overrides && acc.tenant_overrides[tenantId]) {
              return { ...acc, ...acc.tenant_overrides[tenantId] };
            }
            return acc;
          });

        setAccounts(filteredAccounts);
        
        // Extract unique countries from accounts
        const countries = [...new Set(filteredAccounts.map(a => a.country).filter(Boolean))].sort();
        setAvailableCountries(countries);
        const sectors = [...new Set(filteredAccounts.map(a => a.sector).filter(Boolean))].sort();
        setAvailableSectors(sectors);
      } catch (error) {
        console.error('Error loading accounts:', error);
      }
    };
    loadAccounts();
  }, [currentTenant, contextUser, tenantId]);

  // Load people once per tenant change (instead of every loadData call)
  useEffect(() => {
    if (!contextUser?.id) return;
    const loadPeople = async () => {
      const peopleTenantFilter = tenantId === 'smowl'
        ? 'smowl'
        : { $in: [tenantId, 'global'] };
      try {
        const allPeople = await PersonFinal.filter(
          { tenant_id: peopleTenantFilter, is_visible: { $ne: false } },
          '-persona_fit_score',
          10000
        );
        const rows = Array.isArray(allPeople) ? allPeople : [];
        const merged = rows.map((person) => {
          if (person?.tenant_overrides && person.tenant_overrides[tenantId]) {
            return { ...person, ...person.tenant_overrides[tenantId] };
          }
          return person;
        });
        setPeople(merged);
      } catch (err) {
        console.warn('Outbox: people fetch failed, falling back to list()', err);
        try {
          const listed = await PersonFinal.list();
          const rows = Array.isArray(listed) ? listed : [];
          const merged = rows.map((person) => {
            if (person?.tenant_overrides && person.tenant_overrides[tenantId]) {
              return { ...person, ...person.tenant_overrides[tenantId] };
            }
            return person;
          });
          setPeople(merged);
        } catch {
          setPeople([]);
        }
      }
    };
    loadPeople();
  }, [tenantId, contextUser?.id]);

  // Debounce search - wait 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Debounce hide-recent filter — avoid full-page reload on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedHideRecentWorkdays(hideRecentWorkdays);
    }, 500);
    return () => clearTimeout(timer);
  }, [hideRecentWorkdays]);

  // Invalidate in-flight outbox fetches when tenant changes
  useEffect(() => {
    outboxLoadRequestRef.current += 1;
    hasLoadedOutboxOnceRef.current = false;
  }, [tenantId]);

  // Trigger search when debounced value changes
  useEffect(() => {
    setCurrentPage(1);
    loadData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const loadData = async (page = 1) => {
    if (!contextUser?.id) return;
    const requestId = ++outboxLoadRequestRef.current;
    const showFullPageLoader = !hasLoadedOutboxOnceRef.current;
    if (showFullPageLoader) setIsLoading(true);
    else setIsRefreshing(true);
    try {
      // Load drafts with pagination
      const offset = (page - 1) * ITEMS_PER_PAGE;
      let url = `${API_BASE_URL}/api/outbox?tenant_id=${tenantId}&user_id=${contextUser.id}&limit=${ITEMS_PER_PAGE}&offset=${offset}`;
      
      // Status (multi-select): pass repeated params
      if (statusFilter.size > 0) {
        Array.from(statusFilter).forEach((s) => {
          url += `&status=${encodeURIComponent(s)}`;
        });
      } else {
        url += `&status=draft_ready`; // Default when nothing selected
      }

      // Tier (multi-select)
      if (tierFilter.size > 0) {
        Array.from(tierFilter).forEach((t) => {
          url += `&tier=${encodeURIComponent(t)}`;
        });
      }

      // Channel (multi-select)
      if (channelFilter.size > 0) {
        Array.from(channelFilter).forEach((c) => {
          url += `&channel=${encodeURIComponent(c)}`;
        });
      }

      // Touch (multi-select)
      if (touchFilter.size > 0) {
        Array.from(touchFilter).forEach((t) => {
          url += `&touch=${encodeURIComponent(t)}`;
        });
      }

      // Country (multi-select)
      if (countryFilter.size > 0) {
        Array.from(countryFilter).forEach((c) => {
          url += `&country=${encodeURIComponent(c)}`;
        });
      }

      // Sector (multi-select) — same shape as country.
      if (sectorFilter.size > 0) {
        Array.from(sectorFilter).forEach((sec) => {
          url += `&sector=${encodeURIComponent(sec)}`;
        });
      }
      if (socialPresenceFilter !== 'all') {
        url += `&social_presence_min=${encodeURIComponent(socialPresenceFilter)}`;
      }
      if (personaFitFilter !== 'all') {
        const fitMin = Number(personaFitFilter);
        if (Number.isFinite(fitMin) && fitMin > 0) {
          url += `&persona_fit_min=${encodeURIComponent(String(fitMin))}`;
        }
      }
      if (archetypeFilter && archetypeFilter !== 'all') {
        url += `&archetype_name=${encodeURIComponent(archetypeFilter)}`;
      }

      // Product context (single-select for now)
      if (productContextFilter && productContextFilter !== 'all') {
        url += `&product_context=${encodeURIComponent(productContextFilter)}`;
      }

      if (playbookFilter) {
        url += `&playbookKey=${encodeURIComponent(normalizeOutboxPlaybookKey(playbookFilter))}`;
      }
      if (sequenceEventFilterId) {
        url += `&sequence_event_id=${encodeURIComponent(sequenceEventFilterId)}`;
      }
      // Add server-side search (using debounced value)
      if (debouncedSearch.trim()) {
        url += `&search=${encodeURIComponent(debouncedSearch.trim())}`;
      }

      // Server-side date filtering for generated filter
      if (generatedFilter && generatedFilter !== 'all') {
        const now = new Date();
        let created_after = null;
        let created_before = null;

        if (generatedFilter === 'last_hour') {
          created_after = new Date(now.getTime() - 60 * 60 * 1000).toISOString();
        } else if (generatedFilter === 'last_24_hours') {
          created_after = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
        } else if (generatedFilter === 'today') {
          const todayStart = new Date(now);
          todayStart.setHours(0, 0, 0, 0);
          created_after = todayStart.toISOString();
        } else if (generatedFilter === 'this_week') {
          const todayLocal = new Date(now);
          todayLocal.setHours(0, 0, 0, 0);
          const dayOfWeek = todayLocal.getDay();
          const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
          const thisMonday = new Date(todayLocal);
          thisMonday.setDate(todayLocal.getDate() - daysFromMonday);
          created_after = thisMonday.toISOString();
        } else if (generatedFilter === 'last_week') {
          const todayLocal = new Date(now);
          todayLocal.setHours(0, 0, 0, 0);
          const dayOfWeek = todayLocal.getDay();
          const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
          const thisMonday = new Date(todayLocal);
          thisMonday.setDate(todayLocal.getDate() - daysFromMonday);
          
          const lastWeekStart = new Date(thisMonday);
          lastWeekStart.setDate(thisMonday.getDate() - 7);
          
          created_after = lastWeekStart.toISOString();
          created_before = thisMonday.toISOString();
        }

        if (created_after) {
          url += `&created_after=${encodeURIComponent(created_after)}`;
        }
        if (created_before) {
          url += `&created_before=${encodeURIComponent(created_before)}`;
        }
      }

      // Due-date chip is sent server-side via date_filter (BE rev 01825). Client-side
      // matchesOutboxDateFilter uses due_at only — null due_at never counts as overdue.

      // Add server-side event filtering (skip 'no_events' - handle client-side only)
      if (eventFilter && eventFilter !== 'everyone') {
        url += `&event_filter=${encodeURIComponent(eventFilter)}`;
      }

      // [2026-06-22] Hide drafts for personas messaged in last N workdays
      {
        const n = parseInt(String(debouncedHideRecentWorkdays || '').trim(), 10);
        if (Number.isFinite(n) && n >= 1) {
          url += `&hide_recent_persona_workdays=${encodeURIComponent(String(Math.min(10, n)))}`;
        }
      }

      // [2026-06-22] Send the date chip to the server so the count is tenant-wide
      // accurate (BE rev 01825). Client-side matchesOutboxDateFilter is belt-and-braces
      // using due_at only (null-safe — never treats null as epoch / overdue).
      if (dateFilter && dateFilter !== 'all') {
        url += `&date_filter=${encodeURIComponent(dateFilter)}`;
      }

      const response = await fetch(url, { credentials: 'include' });
      if (requestId !== outboxLoadRequestRef.current) return;

      if (response.ok) {
        const data = await response.json();
        if (requestId !== outboxLoadRequestRef.current) return;

        let draftsArray = [];
        let totalCount = 0;

        // Handle different response formats
        if (Array.isArray(data)) {
          // Direct array format
          draftsArray = data;
          totalCount = data.length;
        } else if (data && Array.isArray(data.drafts)) {
          // Wrapped format with drafts property
          draftsArray = data.drafts;
          totalCount = resolveOutboxTotalCount(data, draftsArray);
        } else if (data && data.data && Array.isArray(data.data)) {
          // Wrapped format with data property
          draftsArray = data.data;
          totalCount = resolveOutboxTotalCount(data, draftsArray);
        } else {
          console.warn('📧 Unexpected drafts response format:', data);
          if (requestId !== outboxLoadRequestRef.current) return;
          setDrafts([]);
          setPagination({ total_count: 0, total_pages: 1, has_more: false });
          return;
        }
        const annotatedDrafts = withLinkedInSequenceAnnotation(draftsArray);
        const enrichedDrafts = await enrichOutboxDraftsWithSequencePlans(annotatedDrafts, {
          tenantId,
          userId: contextUser.id,
          apiBase: API_BASE_URL,
        });
        if (requestId !== outboxLoadRequestRef.current) return;

        setDrafts(enrichedDrafts);
        const adHocScheduledRows = enrichedDrafts.filter(isAdHocScheduledOutboxDraft);
        console.debug(
          '[outbox_adhoc_scheduled]',
          adHocScheduledRows.length,
          'scheduled ad-hoc rows received',
          adHocScheduledRows.map((d) => d.stepId || d.id),
        );
        hasLoadedOutboxOnceRef.current = true;
        logLinkedInChannelOutboxBatch('Outbox:load', enrichedDrafts);
        setPagination({
          total_count: totalCount,
          total_pages: Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE)),
          has_more: data.pagination?.has_more ?? (totalCount > page * ITEMS_PER_PAGE),
        });
      } else {
        if (requestId !== outboxLoadRequestRef.current) return;
        console.error('📧 Failed to load drafts:', response.status);
        setDrafts([]);
        setPagination({ total_count: 0, total_pages: 1, has_more: false });
      }
    } catch (error) {
      if (requestId !== outboxLoadRequestRef.current) return;
      console.error('Error loading data:', error);
      notify({
        title: 'Error',
        description: 'Failed to load drafts',
        variant: 'destructive'
      });
      setDrafts([]);
      setPagination({ total_count: 0, total_pages: 1, has_more: false });
    } finally {
      if (requestId === outboxLoadRequestRef.current) {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    }
  };
  loadDataLatestRef.current = loadData;

  // Post-generation redirects can land before drafts are visible; refetch shortly after.
  useEffect(() => {
    if (!contextUser?.id || !tenantId) return;
    if (location.pathname.toLowerCase() !== createPageUrl('Outbox').toLowerCase()) return;
    if (!location.state?.refreshOutbox) return;

    const run = () => {
      const fn = loadDataLatestRef.current;
      if (typeof fn === 'function') {
        fn(1);
        setCurrentPage(1);
      }
    };
    const t1 = setTimeout(run, 1500);
    const t2 = setTimeout(run, 4200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [location.key, location.pathname, tenantId, contextUser?.id, location.state?.refreshOutbox]);

  // Load data on mount and when filters change
  useEffect(() => {
    setCurrentPage(1);
    loadData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentTenant,
    statusFilter,
    tierFilter,
    channelFilter,
    touchFilter,
    countryFilter,
    sectorFilter,
    socialPresenceFilter,
    personaFitFilter,
    archetypeFilter,
    productContextFilter,
    playbookFilter,
    sequenceEventFilterId,
    generatedFilter,
    eventFilter,
    debouncedHideRecentWorkdays,
    dateFilter,
  ]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    loadData(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDraftSent = (draftId) => {
    console.log('📧 Marking draft as sent with ID:', draftId);

    const sentDraft = (drafts || []).find((d) => (d.stepId || d.id) === draftId);
    if (sentDraft && isLikelyAdHocOutboxSend(sentDraft)) {
      setLastAdHocFromSentHistory((prev) =>
        bumpLastAdHocPersonaMap(prev, sentDraft, formatAdHocRelativeLabel, new Date())
      );
    }

    // Broadcast outbox update (triggers ad-hoc-history refetch)
    window.dispatchEvent(new CustomEvent('outbox-updated'));
    
    // If we're viewing the "sent" filter, reload the data to show the newly sent item
    if (statusFilter.has('sent') && statusFilter.size === 1) {
      loadData(currentPage);
    } else {
      // Otherwise, remove from current view
      let removedCount = 0;
      setDrafts((prev) => {
        const filtered = Array.isArray(prev)
          ? prev.filter((d) => {
              const draftStepId = d.stepId || d.id;
              const shouldKeep = draftStepId !== draftId;
              if (!shouldKeep) {
                console.log('📧 Removing draft:', { draftStepId, draftId, draft: d });
              }
              return shouldKeep;
            })
          : [];
        removedCount = Array.isArray(prev) ? prev.length - filtered.length : 0;
        console.log('📧 Remaining drafts after removal:', filtered.length);
        return filtered;
      });
      if (removedCount > 0) {
        decrementOutboxPagination(setPagination, removedCount, ITEMS_PER_PAGE, currentPage);
      }
    }
  };

  const handleMarkAsDone = async (draft, event) => {
    event.stopPropagation();
    
    try {
      const stepId = draft.stepId || draft.id;
      const userId = user?.id;
      
      // Get persona_id - primary reference to personas table
      const personaId = draft.persona_id || draft.person_id || draft.personId || draft.person?.persona_id || draft.person?.person_id || draft.person?.id;

      const channelPayload = buildOutboxChannelRecordingPayload(draft);

      const response = await fetch(`${API_BASE_URL}/api/outbox/${stepId}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          tenant_id: tenantId, 
          user_id: userId,
          send_method: 'done',
          subject: draft.subject || '',
          body_html: draft.body_html || draft.bodyHtml || draft.body || '',
          from_email: null,
          include_signature: false,
          // persona_id is the primary reference (source of truth)
          persona_id: personaId,
          person_id: personaId, // Keep for backward compatibility
          person_name: draft.person?.name || draft.person_name,
          person_linkedin_url:
            draft.person?.linkedin_url || draft.linkedin_url || draft.linkedinUrl,
          account_id: draft.account_id || draft.account?.id,
          account_name: draft.account_name || draft.account?.name,
          is_temporary: draft?.is_temporary || String(stepId).startsWith('draft-'),
          ...channelPayload,
          ...pickAdHocGenerationMetaForSend(draft),
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('📧 Mark as done failed:', errorText);
        throw new Error(`Failed to mark as done: ${errorText}`);
      }

      notify({
        title: 'Marked as done',
        description: `Message to ${draft.person?.name} marked as complete`,
      });

      handleDraftSent(stepId);
    } catch (error) {
      console.error('Error marking as done:', error);
      notify({
        title: 'Failed to mark as done',
        description: error.message || 'Please try again',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteClick = (draft, event) => {
    event.stopPropagation();
    setDeleteTarget({ type: 'single', draft });
  };

  const handleBulkDeleteClick = () => {
    if (selectedDraftIds.size === 0) return;
    setDeleteTarget({ type: 'bulk', count: selectedDraftIds.size });
  };

  const getConnectedEmailProviderTokens = (uid) =>
    resolveEmailProviderTokens({
      tenantId,
      userId: uid,
      preferredOutlookAlias: user?.preferred_outlook_alias,
      preferredGmailAlias: user?.preferred_gmail_alias,
    });

  const buildProviderAwareError = async (response, provider) => {
    const raw = await response.text();
    let payload = null;
    try {
      payload = raw ? JSON.parse(raw) : null;
    } catch {
      payload = null;
    }

    if (payload?.needs_auth) {
      const targetProvider = String(payload?.error || '').toLowerCase().includes('gmail')
        ? 'gmail'
        : String(payload?.error || '').toLowerCase().includes('outlook')
          ? 'outlook'
          : provider;
      const authUrl = payload?.auth_url;
      if (authUrl && targetProvider === provider) {
        return `Your ${provider === 'outlook' ? 'Outlook' : 'Gmail'} session expired. Reconnect it in Settings and try again.`;
      }
      if (targetProvider !== provider) {
        return `Backend rejected ${provider === 'outlook' ? 'Outlook' : 'Gmail'} tokens and asked for ${targetProvider}.`;
      }
    }

    return raw || 'Unknown send error';
  };

  const handleBulkSend = async () => {
    if (selectedDraftIds.size === 0) return;

    // Filter to only drafts that are ready to send
    const draftsToSend = filteredDrafts.filter(draft => {
      const draftId = draft.stepId || draft.id;
      return selectedDraftIds.has(draftId) && draft.status === 'draft_ready';
    });

    if (draftsToSend.length === 0) {
      notify({
        title: 'No drafts to send',
        description: 'Only drafts with "Ready to send" status can be sent',
        variant: 'destructive'
      });
      return;
    }

    // [2026-06-22] Dedupe by persona — at most one send per persona per batch.
    const byPersona = new Map();
    let skippedDupCount = 0;
    for (const d of draftsToSend) {
      const pid =
        d.persona_id || d.person_id || d.personId ||
        d.person?.persona_id || d.person?.person_id || d.person?.id;
      if (!pid) {
        byPersona.set(`stepId::${d.stepId || d.id}`, d);
        continue;
      }
      const prev = byPersona.get(String(pid));
      if (!prev) {
        byPersona.set(String(pid), d);
      } else {
        skippedDupCount++;
        const prevStage = Number(prev.stage ?? prev.stage_no ?? 0);
        const curStage = Number(d.stage ?? d.stage_no ?? 0);
        if (curStage > prevStage) byPersona.set(String(pid), d);
      }
    }
    const dedupedDraftsToSend = Array.from(byPersona.values());

    if (skippedDupCount > 0) {
      notify({
        title: `Skipped ${skippedDupCount} duplicate${skippedDupCount > 1 ? 's' : ''}`,
        description:
          `Multiple drafts targeted the same persona${skippedDupCount > 1 ? 's' : ''}. ` +
          `Sending the latest touch only — the others stay in your outbox.`,
      });
    }

    setIsSending(true);
    const userId = user?.id;

    try {
      const { provider } = await getConnectedEmailProviderTokens(userId);
      const preferredAlias = provider === 'outlook'
        ? (user?.preferred_outlook_alias || null)
        : (user?.preferred_gmail_alias || null);

      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      const BULK_SEND_CONCURRENCY = 2;

      const sendOne = async (draft) => {
        const stepId = draft.stepId || draft.id;
        const personaId = draft.persona_id || draft.person_id || draft.personId || draft.person?.persona_id || draft.person?.person_id || draft.person?.id;
        let recipientEmail = draft.person?.email || draft.person_email;

        if (!recipientEmail && personaId) {
          try {
            const personaResponse = await fetch(
              `${API_BASE_URL}/api/analytics-v2/persona/${personaId}?tenant_id=${tenantId}`,
              { credentials: 'include' }
            );
            if (personaResponse.ok) {
              const persona = await personaResponse.json();
              recipientEmail = persona.email;
            }
          } catch (err) {
            console.warn('⚠️ Could not fetch email from personas table:', err);
          }
        }

        if (!recipientEmail) {
          return { success: false, error: `${draft.person?.name || 'Unknown'}: No email address`, stepId };
        }

        const response = await fetch(`${API_BASE_URL}/api/outbox/${stepId}/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            tenant_id: tenantId,
            user_id: userId,
            provider,
            email_provider: provider,
            subject: draft.subject || '',
            body_html: draft.body_html || '',
            send_method: 'now',
            from_email: preferredAlias,
            include_signature: true,
            persona_id: personaId,
            recipient_email: recipientEmail,
            person_id: personaId,
            person_name: draft.person?.name,
            account_id: draft.account?.id,
            account_name: draft.account?.name,
            channel: draft.channel,
            ...pickAdHocGenerationMetaForSend(draft),
          })
        });

        if (response.status === 409) {
          const blockedData = await response.json().catch(() => ({}));
          if (blockedData?.code === 'RESEND_BLOCKED') {
            return {
              success: false,
              blocked: true,
              stepId,
              recipientEmail: recipientEmail || draft.person?.email || draft.person_email || 'this recipient',
              retryAfterSeconds: Number(blockedData?.retry_after_seconds) || 0,
              lastSentAt: blockedData?.last_sent_at || null,
              backendMessage: blockedData?.error || '',
              cooldownWindowHours: Number(blockedData?.cooldown_window_hours) || 0,
              cooldownLastChannel: String(blockedData?.cooldown_last_channel || '').trim() || null,
              draft,
              provider,
              preferredAlias,
              userId,
            };
          }
        }

        if (!response.ok) {
          const errorText = await buildProviderAwareError(response, provider);
          return { success: false, error: `${draft.person?.name || 'Unknown'}: ${errorText}`, stepId };
        }
        return { success: true, stepId };
      };

      async function sendBatch(draftsBatch, { concurrency = 2 } = {}) {
        const queue = [...draftsBatch];
        const results = [];
        const worker = async () => {
          while (queue.length) {
            const draft = queue.shift();
            if (!draft) break;
            try {
              const r = await sendOne(draft);
              results.push({ ok: true, draft, r });
            } catch (err) {
              results.push({ ok: false, draft, err });
            }
          }
        };
        await Promise.all(
          Array.from({ length: Math.max(1, concurrency) }, () => worker())
        );
        return results;
      }

      const batchResults = await sendBatch(dedupedDraftsToSend, { concurrency: BULK_SEND_CONCURRENCY });

      for (const item of batchResults) {
        const r = item?.r;
        if (item?.ok && r?.success) {
          successCount++;
          handleDraftSent(r.stepId);
        } else if (item?.ok && r?.blocked) {
          errorCount++;
          const sinceSeconds = r.lastSentAt
            ? Math.max(0, Math.floor((Date.now() - new Date(r.lastSentAt).getTime()) / 1000))
            : null;
          setResendBlocked({
            step: r.draft,
            stepId: r.stepId,
            recipientEmail: r.recipientEmail,
            retryAfterSeconds: r.retryAfterSeconds,
            secondsAgo: sinceSeconds,
            lastSentAt: r.lastSentAt,
            message: r.backendMessage,
            cooldownWindowHours: r.cooldownWindowHours,
            cooldownLastChannel: r.cooldownLastChannel,
            provider: r.provider,
            preferredAlias: r.preferredAlias,
            userId: r.userId,
          });
          setResendBlockedSecondsLeft(Math.max(0, r.retryAfterSeconds));
        } else {
          errorCount++;
          if (r?.error) {
            errors.push(r.error);
          } else {
            const message = item?.err?.message || 'Unknown error';
            errors.push(`${item?.draft?.person?.name || 'Unknown'}: ${message}`);
          }
        }
      }

      // Show results
      if (successCount > 0) {
        notify({
          title: `${successCount} message${successCount > 1 ? 's' : ''} sent via ${provider === 'outlook' ? 'Outlook' : 'Gmail'}!`,
          description: errorCount > 0 
            ? `${errorCount} failed. Check console for details.`
            : 'All selected messages have been sent.',
        });
      }

      if (errorCount > 0 && successCount === 0) {
        notify({
          title: 'Failed to send messages',
          description: errors.slice(0, 3).join('; ') + (errors.length > 3 ? '...' : ''),
          variant: 'destructive'
        });
      }

      // Clear selection after sending
      setSelectedDraftIds(new Set());
    } catch (error) {
      console.error('Error in bulk send:', error);
      notify({
        title: 'Failed to send messages',
        description: error.message || 'Please try again',
        variant: 'destructive'
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleResendBlockedRetryNow = async () => {
    if (!resendBlocked?.stepId || !resendBlocked?.step) return;
    setIsHandlingResendBlocked(true);
    try {
      const draft = resendBlocked.step;
      const stepId = resendBlocked.stepId;
      const personaId = draft.persona_id || draft.person_id || draft.personId || draft.person?.persona_id || draft.person?.person_id || draft.person?.id;
      let recipientEmail = draft.person?.email || draft.person_email;
      if (!recipientEmail && personaId) {
        try {
          const personaResponse = await fetch(
            `${API_BASE_URL}/api/analytics-v2/persona/${personaId}?tenant_id=${tenantId}`,
            { credentials: 'include' }
          );
          if (personaResponse.ok) {
            const persona = await personaResponse.json();
            recipientEmail = persona.email;
          }
        } catch {}
      }
      if (!recipientEmail) throw new Error('Recipient email not found');
      const { provider } = await getConnectedEmailProviderTokens(resendBlocked.userId || user?.id);
      const preferredAlias = provider === 'outlook'
        ? (user?.preferred_outlook_alias || null)
        : (user?.preferred_gmail_alias || null);
      const response = await fetch(`${API_BASE_URL}/api/outbox/${stepId}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          tenant_id: tenantId,
          user_id: user?.id,
          provider,
          email_provider: provider,
          subject: draft.subject || '',
          body_html: draft.body_html || '',
          send_method: 'now',
          from_email: preferredAlias,
          include_signature: true,
          persona_id: personaId,
          recipient_email: recipientEmail,
          person_id: personaId,
          person_name: draft.person?.name,
          account_id: draft.account?.id,
          account_name: draft.account?.name,
          channel: draft.channel,
          ...pickAdHocGenerationMetaForSend(draft),
        })
      });
      if (!response.ok) throw new Error(await buildProviderAwareError(response, provider));
      notify({ title: 'Message sent', description: `Sent to ${draft.person?.name || recipientEmail}` });
      handleDraftSent(stepId);
      setResendBlocked(null);
      setResendBlockedSecondsLeft(0);
    } catch (error) {
      notify({ title: 'Failed to send', description: error.message || 'Please try again', variant: 'destructive' });
    } finally {
      setIsHandlingResendBlocked(false);
    }
  };

  const handleResendBlockedSchedule = async () => {
    if (!resendBlocked?.stepId || !resendBlocked?.step) return;
    setIsHandlingResendBlocked(true);
    try {
      const draft = resendBlocked.step;
      const stepId = resendBlocked.stepId;
      const personaId = draft.persona_id || draft.person_id || draft.personId || draft.person?.persona_id || draft.person?.person_id || draft.person?.id;
      let recipientEmail = draft.person?.email || draft.person_email;
      if (!recipientEmail && personaId) {
        try {
          const personaResponse = await fetch(
            `${API_BASE_URL}/api/analytics-v2/persona/${personaId}?tenant_id=${tenantId}`,
            { credentials: 'include' }
          );
          if (personaResponse.ok) {
            const persona = await personaResponse.json();
            recipientEmail = persona.email;
          }
        } catch {}
      }
      if (!recipientEmail) throw new Error('Recipient email not found');
      const { provider } = await getConnectedEmailProviderTokens(resendBlocked.userId || user?.id);
      const preferredAlias = provider === 'outlook'
        ? (user?.preferred_outlook_alias || null)
        : (user?.preferred_gmail_alias || null);
      const scheduledAt = new Date(Date.now() + 5 * 60 * 1000).toISOString();
      const response = await fetch(`${API_BASE_URL}/api/outbox/${stepId}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          tenant_id: tenantId,
          user_id: user?.id,
          provider,
          email_provider: provider,
          subject: draft.subject || '',
          body_html: draft.body_html || '',
          send_method: 'scheduled',
          scheduled_at: scheduledAt,
          from_email: preferredAlias,
          include_signature: true,
          persona_id: personaId,
          recipient_email: recipientEmail,
          person_id: personaId,
          person_name: draft.person?.name,
          account_id: draft.account?.id,
          account_name: draft.account?.name,
          channel: draft.channel,
          ...pickAdHocGenerationMetaForSend(draft),
        })
      });
      if (!response.ok) throw new Error(await buildProviderAwareError(response, provider));
      notify({ title: 'Scheduled', description: 'Message queued for 5 minutes from now.' });
      handleDraftSent(stepId);
      setResendBlocked(null);
      setResendBlockedSecondsLeft(0);
    } catch (error) {
      notify({ title: 'Failed to schedule', description: error.message || 'Please try again', variant: 'destructive' });
    } finally {
      setIsHandlingResendBlocked(false);
    }
  };

  const handleBulkSchedule = async () => {
    if (selectedDraftIds.size === 0) return;
    setIsScheduling(true);
    try {
      const stepIds = Array.from(selectedDraftIds);
      const response = await fetch(`${API_BASE_URL}/api/outbox/bulk/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ stepIds, tenant_id: tenantId, user_id: user?.id }),
      });
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || 'Schedule failed');
      }
      const result = await response.json();
      const scheduled = Number(result.scheduled || 0);
      const nonEmail = Array.isArray(result.skipped_non_email) ? result.skipped_non_email.length : 0;
      const noTime = Array.isArray(result.skipped_no_time) ? result.skipped_no_time.length : 0;
      const notReady = Array.isArray(result.skipped_not_ready) ? result.skipped_not_ready.length : 0;
      const errCount = Array.isArray(result.errors) ? result.errors.length : 0;

      const skipParts = [];
      if (nonEmail > 0) skipParts.push(`${nonEmail} non-email`);
      if (noTime > 0) skipParts.push(`${noTime} without a scheduled time`);
      if (notReady > 0) skipParts.push(`${notReady} not in draft state`);
      if (errCount > 0) skipParts.push(`${errCount} error${errCount === 1 ? '' : 's'}`);

      if (scheduled > 0) {
        notify({
          title: `${scheduled} message${scheduled === 1 ? '' : 's'} scheduled`,
          description: skipParts.length > 0
            ? `Skipped: ${skipParts.join(', ')}.`
            : 'They will fire at the time earmarked on each draft.',
        });
        const scheduledIds = new Set(result.scheduled_step_ids || []);
        let removedCount = 0;
        setDrafts((prev) => {
          if (!Array.isArray(prev)) return [];
          const next = prev.filter((d) => !scheduledIds.has(d.stepId || d.id));
          removedCount = prev.length - next.length;
          return next;
        });
        if (removedCount > 0) {
          decrementOutboxPagination(setPagination, removedCount, ITEMS_PER_PAGE, currentPage);
        }
        setSelectedDraftIds(new Set());
        window.dispatchEvent(new CustomEvent('outbox-updated'));
      } else {
        notify({
          title: 'Nothing scheduled',
          description: skipParts.length > 0
            ? `All ${selectedDraftIds.size} skipped: ${skipParts.join(', ')}.`
            : 'No eligible drafts in the selection.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      console.error('Bulk schedule error:', err);
      notify({
        title: 'Failed to schedule',
        description: err.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setIsScheduling(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    setIsDeleting(true);
    try {
      const userId = user?.id;

      if (deleteTarget.type === 'single') {
        const stepId = deleteTarget.draft.stepId || deleteTarget.draft.id;
        const response = await fetch(
          `${API_BASE_URL}/api/outbox/${stepId}?tenant_id=${tenantId}&user_id=${userId}`,
          { method: 'DELETE', credentials: 'include' }
        );

        if (!response.ok) throw new Error('Delete failed');

        // Remove from UI
        let removedCount = 0;
        setDrafts((prev) => {
          const next = prev.filter((d) => {
            const draftStepId = d.stepId || d.id;
            return draftStepId !== stepId;
          });
          removedCount = prev.length - next.length;
          return next;
        });
        if (removedCount > 0) {
          decrementOutboxPagination(setPagination, removedCount, ITEMS_PER_PAGE, currentPage);
        }

        notify({
          title: 'Draft cancelled',
          description: `Removed draft to ${deleteTarget.draft.person?.name}`,
        });
      } else {
        // Bulk delete
        const stepIds = Array.from(selectedDraftIds);
        const response = await fetch(`${API_BASE_URL}/api/outbox/bulk/delete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ stepIds, tenant_id: tenantId, user_id: userId })
        });

        if (!response.ok) throw new Error('Bulk delete failed');

        // Remove from UI
        const idsToRemove = new Set(selectedDraftIds);
        let removedCount = 0;
        setDrafts((prev) => {
          const next = prev.filter((d) => {
            const draftStepId = d.stepId || d.id;
            return !idsToRemove.has(draftStepId);
          });
          removedCount = prev.length - next.length;
          return next;
        });
        if (removedCount > 0) {
          decrementOutboxPagination(setPagination, removedCount, ITEMS_PER_PAGE, currentPage);
        }

        setSelectedDraftIds(new Set());

        notify({
          title: `${deleteTarget.count} draft${deleteTarget.count > 1 ? 's' : ''} cancelled`,
          description: 'Removed from your outbox',
        });
      }
    } catch (error) {
      console.error('Delete error:', error);
      notify({
        title: 'Delete failed',
        description: error.message || 'Please try again',
        variant: 'destructive'
      });
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleToggleSelect = (draftId) => {
    setSelectedDraftIds(prev => {
      const next = new Set(prev);
      if (next.has(draftId)) {
        next.delete(draftId);
      } else {
        next.add(draftId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedDraftIds.size === filteredDrafts.length) {
      setSelectedDraftIds(new Set());
    } else {
      const allIds = filteredDrafts.map(d => d.stepId || d.id);
      setSelectedDraftIds(new Set(allIds));
    }
  };

  // Helper to get last sent message info
  const getLastSentInfo = (draft) => {
    const sentAt = draft.last_sent_at || draft.lastSentAt || draft.sent_at || draft.sentAt || 
                   draft.completed_at || draft.completedAt;
    
    if (!sentAt) return null;

    try {
      const date = typeof sentAt === 'string' ? parseISO(sentAt) : new Date(sentAt);
      if (isNaN(date.getTime())) return null;

      const lastChannel =
        draft.last_sent_channel || draft.lastSentChannel || draft.channel || 'email';
      const channelName = getChannelDisplayLabel(
        isLinkedInOutboxChannel({ channel: lastChannel }) ? draft : { channel: lastChannel },
      );

      // Compare calendar dates in LOCAL timezone
      const now = new Date();
      
      // Get local date strings for comparison (YYYY-MM-DD format)
      const sentDateLocal = date.toLocaleDateString('en-CA');
      const todayLocal = now.toLocaleDateString('en-CA');
      
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayLocal = yesterday.toLocaleDateString('en-CA');
      
      // Calculate days difference based on calendar dates
      const sentDateObj = new Date(sentDateLocal);
      const todayDateObj = new Date(todayLocal);
      const diffDays = Math.round((todayDateObj - sentDateObj) / (1000 * 60 * 60 * 24));

      let dateStr;
      if (sentDateLocal === todayLocal) {
        // Today - show local time
        dateStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      } else if (sentDateLocal === yesterdayLocal) {
        dateStr = 'Yesterday';
      } else if (diffDays < 7) {
        dateStr = date.toLocaleDateString([], { weekday: 'short' });
      } else if (diffDays < 365) {
        dateStr = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
      } else {
        dateStr = date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
      }

      return { 
        channel: channelName, 
        date: dateStr, 
        fullDate: date.toLocaleDateString([], { 
          month: 'short', 
          day: 'numeric', 
          year: 'numeric', 
          hour: 'numeric', 
          minute: '2-digit' 
        })
      };
    } catch (e) {
      console.warn('Failed to parse last sent date:', e);
      return null;
    }
  };

  const getPersonaCandidateIds = (draft) => {
    return [
      draft?.persona_id,
      draft?.person_id,
      draft?.personId,
      draft?.person?.persona_id,
      draft?.person?.person_id,
      draft?.person?.id,
    ]
      .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
      .map((v) => String(v));
  };

  const toValidDate = (value) => {
    if (!value) return null;
    try {
      const date = typeof value === 'string' ? parseISO(value) : new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  };

  const formatScheduledLabel = (date) => format(date, 'MMM d, h:mm a');

  const formatRelativeDateLabel = (date) => {
    const now = new Date();
    const sentDateLocal = date.toLocaleDateString('en-CA');
    const todayLocal = now.toLocaleDateString('en-CA');
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayLocal = yesterday.toLocaleDateString('en-CA');
    const sentDateObj = new Date(sentDateLocal);
    const todayDateObj = new Date(todayLocal);
    const diffDays = Math.round((todayDateObj - sentDateObj) / (1000 * 60 * 60 * 24));

    if (sentDateLocal === todayLocal) {
      return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }
    if (sentDateLocal === yesterdayLocal) return 'Yesterday';
    if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'short' });
    if (diffDays < 365) return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const fetchLastAdHocFromSentHistory = useCallback(async () => {
    if (!tenantId || !contextUser?.id) return;
    try {
      const url =
        `${API_BASE_URL}/api/outbox/ad-hoc-history` +
        `?tenant_id=${tenantId}` +
        `&user_id=${encodeURIComponent(contextUser.id)}` +
        `&limit=${AD_HOC_SENT_HISTORY_LIMIT}`;
      const res = await fetch(url, { credentials: 'include', cache: 'no-store' });
      if (!res.ok) {
        console.warn('Outbox: ad-hoc-history fetch failed', res.status, await res.text().catch(() => ''));
        return;
      }
      const data = await res.json();
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      const map = buildLastAdHocPersonaMapFromRows(rows, toValidDate, formatRelativeDateLabel);
      setLastAdHocFromSentHistory(map);
    } catch (e) {
      console.warn('Outbox: last ad-hoc history fetch failed', e);
    }
  }, [tenantId, contextUser?.id]);

  // [2026-07-01] Parallel fetch for the last PLAYBOOK touch sent per persona.
  // Feeds the "Last playbook touch sent: T{stage} {channel} · {date}" line
  // on the Outbox draft card. Fixes the Zalizah Awang Long anomaly where 5
  // delivered playbook emails weren't reflected on her row.
  const fetchLastPlaybookFromSentHistory = useCallback(async () => {
    if (!tenantId || !contextUser?.id) return;
    try {
      const url =
        `${API_BASE_URL}/api/outbox/scheduled-history` +
        `?tenant_id=${tenantId}` +
        `&user_id=${encodeURIComponent(contextUser.id)}` +
        `&limit=${AD_HOC_SENT_HISTORY_LIMIT}`;
      const res = await fetch(url, { credentials: 'include', cache: 'no-store' });
      if (!res.ok) {
        console.warn('Outbox: scheduled-history fetch failed', res.status);
        return;
      }
      const data = await res.json();
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      const map = buildLastPlaybookPersonaMapFromRows(rows, toValidDate, formatRelativeDateLabel);
      setLastPlaybookFromSentHistory(map);
    } catch (e) {
      console.warn('Outbox: last playbook history fetch failed', e);
    }
  }, [tenantId, contextUser?.id]);

  const fetchScheduledFromHistory = useCallback(async () => {
    if (!tenantId || !contextUser?.id) return;

    const applyScheduledRows = (data) => {
      const rows = extractOutboxRowsFromResponse(data);
      setScheduledFromHistory(buildScheduledPersonaMapFromRows(rows, toValidDate, formatScheduledLabel));
    };

    const fetchScheduledFallback = async () => {
      const fallbackUrl =
        `${API_BASE_URL}/api/outbox` +
        `?tenant_id=${tenantId}` +
        `&user_id=${encodeURIComponent(contextUser.id)}` +
        `&limit=${SCHEDULED_HISTORY_LIMIT}` +
        `&offset=0` +
        `&status=scheduled`;
      const fb = await fetch(fallbackUrl, { credentials: 'include', cache: 'no-store' });
      if (!fb.ok) {
        console.warn('Outbox: scheduled fallback fetch failed', fb.status);
        return;
      }
      applyScheduledRows(await fb.json());
    };

    try {
      const skipPrimary =
        typeof sessionStorage !== 'undefined' &&
        sessionStorage.getItem(SCHEDULED_HISTORY_UNSUPPORTED_KEY) === '1';

      if (!skipPrimary) {
        const historyUrl =
          `${API_BASE_URL}/api/outbox/upcoming-scheduled` +
          `?tenant_id=${tenantId}` +
          `&user_id=${encodeURIComponent(contextUser.id)}` +
          `&limit=${SCHEDULED_HISTORY_LIMIT}`;
        const res = await fetch(historyUrl, { credentials: 'include', cache: 'no-store' });
        if (res.ok) {
          applyScheduledRows(await res.json());
          return;
        }
        if (res.status === 404) {
          try {
            sessionStorage.setItem(SCHEDULED_HISTORY_UNSUPPORTED_KEY, '1');
          } catch {
            // ignore quota / private mode
          }
        } else {
          console.warn('Outbox: upcoming-scheduled fetch failed', res.status, await res.text().catch(() => ''));
        }
      }

      await fetchScheduledFallback();
    } catch (e) {
      console.warn('Outbox: scheduled history fetch failed', e);
    }
  }, [tenantId, contextUser?.id]);

  useEffect(() => {
    fetchLastAdHocFromSentHistory();
    fetchLastPlaybookFromSentHistory();
    fetchScheduledFromHistory();
  }, [fetchLastAdHocFromSentHistory, fetchLastPlaybookFromSentHistory, fetchScheduledFromHistory]);

  useEffect(() => {
    const onOutboxUpdated = () => {
      fetchLastAdHocFromSentHistory();
      fetchLastPlaybookFromSentHistory();
      fetchScheduledFromHistory();
    };
    window.addEventListener('outbox-updated', onOutboxUpdated);
    return () => window.removeEventListener('outbox-updated', onOutboxUpdated);
  }, [fetchLastAdHocFromSentHistory, fetchLastPlaybookFromSentHistory, fetchScheduledFromHistory]);

  // [2026-07-01] Daily send tally (combines ad-hoc + playbook) for the top
  // banner. Polls every 30s; refetches immediately on any 'outbox-updated'
  // event (the same signal already fired after each send) so the tally
  // reflects a click without waiting for the poll.
  const fetchDailyTally = useCallback(async () => {
    if (!tenantId || !contextUser?.id) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/outbox/daily-send-count?tenant_id=${encodeURIComponent(tenantId)}&user_id=${encodeURIComponent(contextUser.id)}`,
        { credentials: 'include' },
      );
      if (!res.ok) return;
      const data = await res.json();
      setDailyTally({
        count: Number(data?.count || 0),
        threshold: Number(data?.threshold || 200),
        over_threshold: !!data?.over_threshold,
      });
    } catch (e) {
      // Non-fatal; leave prior tally on transient error.
      console.warn('daily-send-count fetch failed:', e?.message);
    }
  }, [tenantId, contextUser?.id]);

  useEffect(() => {
    fetchDailyTally();
    const id = setInterval(fetchDailyTally, 30_000);
    const onOutboxUpdated = () => fetchDailyTally();
    window.addEventListener('outbox-updated', onOutboxUpdated);
    return () => {
      clearInterval(id);
      window.removeEventListener('outbox-updated', onOutboxUpdated);
    };
  }, [fetchDailyTally]);

  const lastAdHocSentByPersonaId = React.useMemo(() => {
    const fromPage = buildLastAdHocPersonaMapFromRows(drafts, toValidDate, formatRelativeDateLabel);
    return mergeLastAdHocPersonaMaps(lastAdHocFromSentHistory, fromPage);
  }, [lastAdHocFromSentHistory, drafts]);

  // [2026-07-01] Just the /scheduled-history map — nothing to merge from
  // the current page's drafts (they're drafts, not sent rows). Kept as a
  // memo for API symmetry with lastAdHocSentByPersonaId.
  const lastPlaybookSentByPersonaId = lastPlaybookFromSentHistory;

  const scheduledByPersonaId = React.useMemo(() => {
    const fromPage = buildScheduledPersonaMapFromRows(drafts, toValidDate, formatScheduledLabel);
    return mergeScheduledPersonaMaps(scheduledFromHistory, fromPage);
  }, [scheduledFromHistory, drafts]);

  const getScheduledInfoForDraft = useCallback(
    (draft) => {
      const currentStepId = String(draft?.stepId || draft?.id || '');
      const items = [];
      const seenStepIds = new Set();
      getPersonaCandidateIds(draft).forEach((id) => {
        const hit = scheduledByPersonaId.get(id);
        if (!hit?.items?.length) return;
        hit.items.forEach((item) => {
          if (item.stepId && item.stepId === currentStepId) return;
          if (item.stepId && seenStepIds.has(item.stepId)) return;
          if (item.stepId) seenStepIds.add(item.stepId);
          items.push(item);
        });
      });
      if (items.length === 0) return null;
      return summarizeScheduledItems(items);
    },
    [scheduledByPersonaId]
  );

  const getLastAdHocInfoForDraft = useCallback(
    (draft) => {
      const candidates = [];
      const raw =
        draft?.last_ad_hoc_sent_at ||
        draft?.lastAdHocSentAt ||
        draft?.persona_last_ad_hoc_sent_at ||
        draft?.person_last_ad_hoc_sent_at;
      const draftDate = toValidDate(raw);
      if (draftDate) {
        const channelName = getChannelDisplayLabel({
          channel:
            draft?.last_ad_hoc_channel ||
            draft?.lastAdHocChannel ||
            draft?.last_ad_hoc_sent_channel ||
            draft?.last_ad_hoc_message_channel ||
            '',
        }) || null;
        candidates.push({
          dateObj: draftDate,
          date: formatRelativeDateLabel(draftDate),
          fullDate: draftDate.toLocaleDateString([], {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          }),
          channel: channelName,
        });
      }
      getPersonaCandidateIds(draft).forEach((id) => {
        const hit = lastAdHocSentByPersonaId.get(id);
        if (hit) candidates.push(hit);
      });
      if (candidates.length === 0) return null;
      return candidates.reduce((best, cur) => (cur.dateObj > best.dateObj ? cur : best));
    },
    [lastAdHocSentByPersonaId]
  );

  // [2026-07-01] Sibling of getLastAdHocInfoForDraft, sourcing the last
  // PLAYBOOK touch sent (from /scheduled-history) for this persona.
  const getLastPlaybookInfoForDraft = useCallback(
    (draft) => {
      const candidates = [];
      getPersonaCandidateIds(draft).forEach((id) => {
        const hit = lastPlaybookSentByPersonaId.get(id);
        if (hit) candidates.push(hit);
      });
      if (candidates.length === 0) return null;
      return candidates.reduce((best, cur) => (cur.dateObj > best.dateObj ? cur : best));
    },
    [lastPlaybookSentByPersonaId]
  );

  // Helper to get persona for a draft
  const getPersonaForDraft = useCallback((draft) => {
    const candidateIds = getPersonaCandidateIds(draft);

    if (candidateIds.length === 0) return null;
    for (const id of candidateIds) {
      const hit = personasById.get(id);
      if (hit) return hit;
    }
    return null;
  }, [personasById]);

  const toFiniteNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const getPersonaFitScore = useCallback((draft, persona) => {
    return toFiniteNumber(
      persona?.persona_fit_score ??
      persona?.fit_score ??
      draft?.persona_fit_score ??
      draft?.personaFitScore ??
      draft?.person?.persona_fit_score ??
      draft?.person?.fit_score ??
      draft?.persona?.persona_fit_score
    );
  }, []);

  const getSocialPresenceScore = (draft, persona) => {
    return toFiniteNumber(
      persona?.social_presence_score ??
      draft?.social_presence_score ??
      draft?.person?.social_presence_score
    );
  };

  const getArchetypeName = (draft, persona) => {
    const raw =
      persona?.archetype_name ??
      persona?.archetype ??
      draft?.person?.archetype_name ??
      draft?.person?.archetype ??
      draft?.archetype_name ??
      draft?.archetype;
    const value = String(raw || '').trim();
    return value || null;
  };

  const getSequenceFromDraft = (draft) => draft?.sequence || {};

  const getCampaignDisplayName = (draft) => {
    const sequence = getSequenceFromDraft(draft);
    const sequenceEventId = sequence?.event_id || draft?.event_id || '';
    const sequenceKeyRaw = sequence?.playbook_id || sequence?.playbook_key || draft?.playbook_id || draft?.playbook_key || '';
    const sequenceKey = normalizeOutboxPlaybookKey(sequenceKeyRaw);

    const catalogMatch = (mergedAvailablePlaybooks || []).find((p) => {
      const keyMatch = normalizeOutboxPlaybookKey(p?.key) === sequenceKey;
      if (!keyMatch) return false;
      if (!sequenceEventId) return true;
      const optionEventId = p?.sequence_event_id || p?.event_id || '';
      return String(optionEventId) === String(sequenceEventId);
    });

    return (
      sequence?.event_name ||
      draft?.event_name ||
      sequence?.sequence_name ||
      draft?.sequence_name ||
      sequence?.playbook_name ||
      draft?.playbook_name ||
      catalogMatch?.name ||
      sequence?.playbook_key ||
      draft?.playbook_key ||
      sequence?.playbook_id ||
      draft?.playbook_id ||
      (String(sequenceKeyRaw || '').toLowerCase().startsWith('event::') ? 'Custom Event Playbook' : null) ||
      'Untitled Campaign'
    );
  };

  const getCampaignIdentity = (draft) => {
    const sequence = getSequenceFromDraft(draft);
    const playbookId = sequence?.playbook_id || null;
    const eventId = sequence?.event_id || draft?.event_id || null;
    const campaignId = getOutboxCampaignKey(draft);
    return {
      campaignId,
      playbookId,
      eventId,
      isEventCampaign: Boolean(eventId),
      displayName: isAdHocScheduledOutboxDraft(draft)
        ? (draft?.playbook_name || draft?.sequence_name || sequence?.playbook_name || sequence?.sequence_name || 'Ad-hoc (scheduled)')
        : getCampaignDisplayName(draft),
    };
  };

  const filteredDrafts = React.useMemo(() => {
    if (!Array.isArray(drafts)) return [];

    const dateFiltered =
      dateFilter && dateFilter !== 'all'
        ? drafts.filter((draft) => matchesOutboxDateFilter(draft, dateFilter))
        : drafts;

    return [...dateFiltered].sort((a, b) => {
      const getCreatedTs = (draft) => {
        const raw =
          draft.created_at ||
          draft.createdAt ||
          draft.generated_at ||
          draft.generatedAt ||
          draft.lastUpdate ||
          draft.last_update ||
          draft.updated_at ||
          draft.updatedAt;
        if (!raw) return 0;
        const t = new Date(raw).getTime();
        return Number.isFinite(t) ? t : 0;
      };
      const ca = getCreatedTs(a);
      const cb = getCreatedTs(b);
      if (cb !== ca) return cb - ca;
      return 0;
    });
  }, [drafts, dateFilter]);

  const groupedDrafts = React.useMemo(() => {
    const adHocScheduledDrafts = [];
    const otherDrafts = [];

    for (const draft of filteredDrafts) {
      if (isAdHocScheduledOutboxDraft(draft)) {
        adHocScheduledDrafts.push(draft);
      } else {
        otherDrafts.push(draft);
      }
    }

    const grouped = otherDrafts.reduce((acc, draft) => {
      const campaign = getCampaignIdentity(draft);
      const campaignKey = String(campaign.campaignId || 'unknown_campaign');
      const accountName = draft.account?.name || draft.account_name || 'Unknown Account';
      if (!acc[campaignKey]) {
        acc[campaignKey] = {
          campaign,
          accounts: {},
          isAdHocScheduledGroup: false,
        };
      } else if (
        acc[campaignKey].campaign.displayName === 'Untitled Campaign' &&
        campaign.displayName !== 'Untitled Campaign'
      ) {
        acc[campaignKey].campaign.displayName = campaign.displayName;
      }
      if (!acc[campaignKey].accounts[accountName]) {
        acc[campaignKey].accounts[accountName] = [];
      }
      acc[campaignKey].accounts[accountName].push(draft);
      return acc;
    }, {});

    if (adHocScheduledDrafts.length > 0) {
      const flat = dedupeOutboxDraftsByStepId(adHocScheduledDrafts).sort(
        (a, b) => getOutboxScheduledSendTimestamp(a) - getOutboxScheduledSendTimestamp(b),
      );
      const sample = flat[0];
      grouped[AD_HOC_SCHEDULED_SEQUENCE_ID] = {
        campaign: {
          campaignId: AD_HOC_SCHEDULED_SEQUENCE_ID,
          playbookId: null,
          eventId: null,
          isEventCampaign: false,
          displayName:
            sample?.playbook_name ||
            sample?.sequence_name ||
            sample?.sequence?.playbook_name ||
            sample?.sequence?.sequence_name ||
            'Ad-hoc (scheduled)',
        },
        accounts: { __scheduled__: flat },
        isAdHocScheduledGroup: true,
      };
    }

    Object.keys(grouped).forEach((campaignKey) => {
      if (campaignKey === AD_HOC_SCHEDULED_SEQUENCE_ID) return;
      Object.keys(grouped[campaignKey].accounts).forEach((accountName) => {
        grouped[campaignKey].accounts[accountName].sort((a, b) => {
          const sa = Number(a.stage ?? a.stage_no ?? 0);
          const sb = Number(b.stage ?? b.stage_no ?? 0);
          return sa - sb;
        });
      });
    });
    return grouped;
  }, [filteredDrafts]);

  useEffect(() => {
    const group = groupedDrafts[AD_HOC_SCHEDULED_SEQUENCE_ID];
    const rendered = group
      ? dedupeOutboxDraftsByStepId(Object.values(group.accounts || {}).flat()).length
      : 0;
    console.debug('[outbox_adhoc_scheduled_render]', rendered, 'rows in Ad-hoc (scheduled) card');
  }, [groupedDrafts]);

  const sortedCampaignEntries = React.useMemo(() => {
    return Object.entries(groupedDrafts).sort(([keyA, groupA], [keyB, groupB]) => {
      const aScheduled = keyA === AD_HOC_SCHEDULED_SEQUENCE_ID || groupA?.isAdHocScheduledGroup;
      const bScheduled = keyB === AD_HOC_SCHEDULED_SEQUENCE_ID || groupB?.isAdHocScheduledGroup;
      if (aScheduled && !bScheduled) return -1;
      if (!aScheduled && bScheduled) return 1;
      return 0;
    });
  }, [groupedDrafts]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#00C2C2]" />
      </div>
    );
  }

  return (
    <>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0A1A2F]">{t('outbox')}</h1>
            <p className="text-sm text-[#5C6B7A] mt-1">
              Showing {filteredDrafts.length} of {pagination.total_count || filteredDrafts.length} draft
              {(pagination.total_count || filteredDrafts.length) !== 1 ? 's' : ''}
              {isRefreshing ? (
                <Loader2 className="inline w-3.5 h-3.5 ml-2 animate-spin text-[#00C2C2] align-middle" />
              ) : null}
            </p>
            {debouncedHideRecentWorkdays && parseInt(debouncedHideRecentWorkdays, 10) >= 1 && (
              <p className="text-xs text-amber-700 mt-0.5">
                Hiding personas messaged in the last {debouncedHideRecentWorkdays} workday{debouncedHideRecentWorkdays === '1' ? '' : 's'}.
              </p>
            )}
          </div>
        </div>

        {/* [2026-07-01] Daily send tally — combined ad-hoc + playbook. Warn
            at the threshold (200/day today). Polls every 30s + refreshes on
            each 'outbox-updated' event so it reacts to sends within seconds. */}
        <div
          className={`flex items-center justify-between rounded-md border px-4 py-3 ${
            dailyTally.over_threshold
              ? 'border-red-300 bg-red-50 text-red-800'
              : dailyTally.count >= Math.floor(dailyTally.threshold * 0.8)
                ? 'border-amber-300 bg-amber-50 text-amber-800'
                : 'border-gray-200 bg-white text-[#0A1A2F]'
          }`}
        >
          <div className="flex items-center gap-3">
            <Mail className={`w-4 h-4 ${dailyTally.over_threshold ? 'text-red-600' : 'text-[#00C2C2]'}`} />
            <span className="text-sm font-medium">Emails sent today</span>
            <span className="text-lg font-bold">
              {dailyTally.count} / {dailyTally.threshold}
            </span>
          </div>
          {dailyTally.over_threshold ? (
            <span className="text-xs font-semibold uppercase tracking-wide">Daily threshold reached — pause new sends</span>
          ) : dailyTally.count >= Math.floor(dailyTally.threshold * 0.8) ? (
            <span className="text-xs font-medium">Approaching threshold</span>
          ) : (
            <span className="text-xs text-[#5C6B7A]">Includes ad-hoc + playbook · resets 00:00 UTC</span>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 md:gap-4">
          <div className="w-full md:flex-1 md:min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder={t('searchDrafts')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setDebouncedSearch(searchTerm); // Triggers immediate search
                  }
                }}
                className="pl-10 text-sm"
              />
            </div>
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full md:w-[150px] text-sm justify-start min-w-0">
                <Filter className="w-4 h-4 mr-2 flex-shrink-0" />
                <span className="flex-1 text-left overflow-hidden text-ellipsis whitespace-nowrap min-w-0">
                  {statusFilter.size === 0 
                    ? 'All Statuses' 
                    : statusFilter.size === 2 && statusFilter.has('draft_ready') && statusFilter.has('scheduled')
                      ? 'Drafts + Scheduled'
                      : statusFilter.size === 1 
                      ? Array.from(statusFilter)[0] === 'draft_ready' ? 'Draft' :
                        Array.from(statusFilter)[0] === 'scheduled' ? 'Scheduled' :
                        Array.from(statusFilter)[0] === 'sent' ? 'Sent' :
                        Array.from(statusFilter)[0] === 'error' ? 'Errors' :
                        Array.from(statusFilter)[0]
                      : `${statusFilter.size} Selected`}
                </span>
                <ChevronDown className="w-4 h-4 opacity-50 flex-shrink-0 ml-1" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[180px] p-2" align="start">
              <div className="space-y-2">
                <div className="font-semibold text-sm mb-2 px-2">Select Status</div>
                {[
                  { value: 'draft_ready', label: 'Draft' },
                  { value: 'scheduled', label: 'Scheduled' },
                  { value: 'sent', label: 'Sent' },
                  { value: 'error', label: 'Errors' }
                ].map(({ value, label }) => {
                  const isSelected = statusFilter.has(value);
                  return (
                    <label
                      key={value}
                      className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-50 cursor-pointer"
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => {
                          setStatusFilter(prev => {
                            const next = new Set(prev);
                            if (checked) {
                              next.add(value);
                            } else {
                              next.delete(value);
                            }
                            return next;
                          });
                        }}
                      />
                      <span className="text-sm cursor-pointer flex-1">{label}</span>
                    </label>
                  );
                })}
                {statusFilter.size > 0 && (
                  <div className="border-t pt-2 mt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-xs"
                      onClick={() => setStatusFilter(new Set(['draft_ready']))}
                    >
                      Reset to Default
                    </Button>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>

          <Select value={socialPresenceFilter} onValueChange={setSocialPresenceFilter}>
            <SelectTrigger className="w-full md:w-[170px] text-sm">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Social Presence" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All social presence</SelectItem>
              {Array.from({ length: 11 }, (_, i) => i * 10).map((value) => (
                <SelectItem key={`social-${value}`} value={String(value)}>
                  Social Presence {value}+
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={personaFitFilter} onValueChange={setPersonaFitFilter}>
            <SelectTrigger className="w-full md:w-[170px] text-sm">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Persona Fit" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All persona fit</SelectItem>
              {Array.from({ length: 11 }, (_, i) => i * 10).map((value) => (
                <SelectItem key={`fit-${value}`} value={String(value)}>
                  Persona Fit {value}+
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={archetypeFilter} onValueChange={setArchetypeFilter}>
            <SelectTrigger className="w-full md:w-[180px] text-sm">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Archetype" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All archetypes</SelectItem>
              {availableArchetypes.map((archetype) => (
                <SelectItem key={`archetype-${archetype}`} value={archetype}>
                  {archetype}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full md:w-[130px] text-sm justify-start min-w-0">
                <Filter className="w-4 h-4 mr-2 flex-shrink-0" />
                <span className="flex-1 text-left overflow-hidden text-ellipsis whitespace-nowrap min-w-0">
                  {tierFilter.size === 0
                    ? 'All Tiers'
                    : tierFilter.size === 1
                      ? `Tier ${String(Array.from(tierFilter)[0]).replace(/^T/i, '')}`
                      : `${tierFilter.size} Selected`}
                </span>
                <ChevronDown className="w-4 h-4 opacity-50 flex-shrink-0 ml-1" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[180px] p-2" align="start">
              <div className="space-y-2">
                <div className="font-semibold text-sm mb-2 px-2">Select Tiers</div>
                {['T1', 'T2', 'T3', 'T4'].map(tier => {
                  const isSelected = tierFilter.has(tier);
                  return (
                    <label
                      key={tier}
                      className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-50 cursor-pointer"
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => {
                          setTierFilter(prev => {
                            const next = new Set(prev);
                            if (checked) {
                              next.add(tier);
                            } else {
                              next.delete(tier);
                            }
                            return next;
                          });
                        }}
                      />
                      <span className="text-sm cursor-pointer flex-1">Tier {tier.slice(1)}</span>
                    </label>
                  );
                })}
                {tierFilter.size > 0 && (
                  <div className="border-t pt-2 mt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-xs"
                      onClick={() => setTierFilter(new Set())}
                    >
                      {t('clearAll')}
                    </Button>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full md:w-[140px] text-sm justify-start min-w-0">
                <Filter className="w-4 h-4 mr-2 flex-shrink-0" />
                <span className="flex-1 text-left overflow-hidden text-ellipsis whitespace-nowrap min-w-0">
                  {touchFilter.size === 0 
                    ? 'All Touches' 
                    : touchFilter.size === 1 
                      ? `Touch ${Array.from(touchFilter)[0]}` 
                      : `${touchFilter.size} Selected`}
                </span>
                <ChevronDown className="w-4 h-4 opacity-50 flex-shrink-0 ml-1" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[180px] p-2" align="start">
              <div className="space-y-2">
                <div className="font-semibold text-sm mb-2 px-2">Select Touches</div>
                {[1, 2, 3, 4, 5, 6].map(touchNum => {
                  const touchStr = String(touchNum);
                  const isSelected = touchFilter.has(touchStr);
                  return (
                    <label
                      key={touchNum}
                      className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-50 cursor-pointer"
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => {
                          setTouchFilter(prev => {
                            const next = new Set(prev);
                            if (checked) {
                              next.add(touchStr);
                            } else {
                              next.delete(touchStr);
                            }
                            return next;
                          });
                        }}
                      />
                      <span className="text-sm cursor-pointer flex-1">
                        Touch {touchNum}
                      </span>
                    </label>
                  );
                })}
                {touchFilter.size > 0 && (
                  <div className="border-t pt-2 mt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-xs"
                      onClick={() => setTouchFilter(new Set())}
                    >
                      {t('clearAll')}
                    </Button>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>

          <div className="flex items-center gap-1.5 h-9 px-2.5 border rounded-md w-full md:w-auto bg-white text-sm">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="flex-shrink-0 text-gray-500 hover:text-gray-700" aria-label="About hide-recent filter">
                    <Filter className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs">
                  Hide drafts for any persona you&apos;ve messaged (email, LinkedIn, call, or Today&apos;s Plan completion)
                  in the last N <strong>workdays</strong> — weekends don&apos;t count. Useful when you&apos;ve already sent
                  to someone outside their playbook cadence and don&apos;t want the overdue draft pestering you.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <span className="text-gray-600 whitespace-nowrap">Hide if messaged in last</span>
            <Input
              type="number"
              min="0"
              max="10"
              value={hideRecentWorkdays}
              onChange={(e) => {
                const v = e.target.value.replace(/[^\d]/g, '');
                setHideRecentWorkdays(v);
              }}
              onBlur={() => {
                let next = hideRecentWorkdays;
                const n = parseInt(hideRecentWorkdays, 10);
                if (Number.isFinite(n) && n > 10) next = '10';
                if (Number.isFinite(n) && n < 0) next = '';
                if (next !== hideRecentWorkdays) setHideRecentWorkdays(next);
                setDebouncedHideRecentWorkdays(next);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  let next = hideRecentWorkdays;
                  const n = parseInt(hideRecentWorkdays, 10);
                  if (Number.isFinite(n) && n > 10) next = '10';
                  if (Number.isFinite(n) && n < 0) next = '';
                  if (next !== hideRecentWorkdays) setHideRecentWorkdays(next);
                  setDebouncedHideRecentWorkdays(next);
                }
              }}
              placeholder="—"
              className="h-7 w-12 px-1 text-center text-sm border-0 focus-visible:ring-1 focus-visible:ring-[#00C2C2] p-0"
            />
            <span className="text-gray-600 whitespace-nowrap">workdays</span>
            {hideRecentWorkdays && (
              <button
                type="button"
                onClick={() => {
                  setHideRecentWorkdays('');
                  setDebouncedHideRecentWorkdays('');
                }}
                className="ml-1 text-gray-400 hover:text-gray-700 text-xs"
                aria-label="Clear hide-recent filter"
              >
                ×
              </button>
            )}
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full md:w-[140px] text-sm justify-start min-w-0">
                <Filter className="w-4 h-4 mr-2 flex-shrink-0" />
                <span className="flex-1 text-left overflow-hidden text-ellipsis whitespace-nowrap min-w-0">
                  {channelFilter.size === 0 
                    ? 'All Channels' 
                    : channelFilter.size === 1 
                      ? Array.from(channelFilter)[0] === 'email' ? 'Email' :
                        Array.from(channelFilter)[0] === 'linkedin' ? 'LinkedIn' :
                        Array.from(channelFilter)[0] === 'call' ? 'Call' :
                        Array.from(channelFilter)[0]
                      : `${channelFilter.size} Selected`}
                </span>
                <ChevronDown className="w-4 h-4 opacity-50 flex-shrink-0 ml-1" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[180px] p-2" align="start">
              <div className="space-y-2">
                <div className="font-semibold text-sm mb-2 px-2">Select Channels</div>
                {[
                  { value: 'email', label: 'Email' },
                  { value: 'linkedin', label: 'LinkedIn' },
                  { value: 'call', label: 'Call' }
                ].map(({ value, label }) => {
                  const isSelected = channelFilter.has(value);
                  return (
                    <label
                      key={value}
                      className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-50 cursor-pointer"
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => {
                          setChannelFilter(prev => {
                            const next = new Set(prev);
                            if (checked) {
                              next.add(value);
                            } else {
                              next.delete(value);
                            }
                            return next;
                          });
                        }}
                      />
                      <span className="text-sm cursor-pointer flex-1">{label}</span>
                    </label>
                  );
                })}
                {channelFilter.size > 0 && (
                  <div className="border-t pt-2 mt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-xs"
                      onClick={() => setChannelFilter(new Set())}
                    >
                      {t('clearAll')}
                    </Button>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>

          <Select value={dateFilter} onValueChange={setDateFilter}>
            <SelectTrigger className="w-full md:w-[140px] text-sm">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Due Today</SelectItem>
              <SelectItem value="tomorrow">Due Tomorrow</SelectItem>
              <SelectItem value="next_7_days">Next 7 Days</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="no_date">No Due Date</SelectItem>
              <SelectItem value="all">All Dates</SelectItem>
            </SelectContent>
          </Select>

          <Select value={generatedFilter} onValueChange={setGeneratedFilter}>
            <SelectTrigger className="w-full md:w-[160px] text-sm">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Generated" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Messages</SelectItem>
              <SelectItem value="last_hour">Last Hour</SelectItem>
              <SelectItem value="last_24_hours">Last 24 Hours</SelectItem>
              <SelectItem value="today">Generated Today</SelectItem>
              <SelectItem value="this_week">This Week</SelectItem>
              <SelectItem value="last_week">Last Week</SelectItem>
            </SelectContent>
          </Select>

          <Select value={eventFilter} onValueChange={setEventFilter}>
            <SelectTrigger className="w-full md:w-40 text-sm">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by Event" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="everyone">Everyone</SelectItem>
              <SelectItem value="BETT2026">BETT2026</SelectItem>
              <SelectItem value="no_events">No Events</SelectItem>
            </SelectContent>
          </Select>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full md:w-[150px] text-sm justify-start min-w-0">
                <Filter className="w-4 h-4 mr-2 flex-shrink-0" />
                <span className="flex-1 text-left overflow-hidden text-ellipsis whitespace-nowrap min-w-0">
                  {countryFilter.size === 0 
                    ? 'All Countries' 
                    : countryFilter.size === 1 
                      ? Array.from(countryFilter)[0]
                      : `${countryFilter.size} Selected`}
                </span>
                <ChevronDown className="w-4 h-4 opacity-50 flex-shrink-0 ml-1" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[200px] max-h-[300px] overflow-y-auto p-2" align="start">
              <div className="space-y-2">
                <div className="font-semibold text-sm mb-2 px-2">Select Countries</div>
                {availableCountries.map(country => {
                  const isSelected = countryFilter.has(country);
                  return (
                    <label
                      key={country}
                      className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-50 cursor-pointer"
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => {
                          setCountryFilter(prev => {
                            const next = new Set(prev);
                            if (checked) {
                              next.add(country);
                            } else {
                              next.delete(country);
                            }
                            return next;
                          });
                        }}
                      />
                      <span className="text-sm cursor-pointer flex-1">{country}</span>
                    </label>
                  );
                })}
                {countryFilter.size > 0 && (
                  <div className="border-t pt-2 mt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-xs"
                      onClick={() => setCountryFilter(new Set())}
                    >
                      {t('clearAll')}
                    </Button>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full md:w-[160px] text-sm justify-start min-w-0">
                <Filter className="w-4 h-4 mr-2 flex-shrink-0" />
                <span className="flex-1 text-left overflow-hidden text-ellipsis whitespace-nowrap min-w-0">
                  {sectorFilter.size === 0
                    ? 'All Sectors'
                    : sectorFilter.size === 1
                      ? Array.from(sectorFilter)[0]
                      : `${sectorFilter.size} Selected`}
                </span>
                <ChevronDown className="w-4 h-4 opacity-50 flex-shrink-0 ml-1" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[260px] max-h-[300px] overflow-y-auto p-2" align="start">
              <div className="space-y-2">
                <div className="font-semibold text-sm mb-2 px-2">Select Sectors</div>
                {availableSectors.map(sector => {
                  const isSelected = sectorFilter.has(sector);
                  return (
                    <label
                      key={sector}
                      className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-50 cursor-pointer"
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => {
                          setSectorFilter(prev => {
                            const next = new Set(prev);
                            if (checked) next.add(sector);
                            else next.delete(sector);
                            return next;
                          });
                        }}
                      />
                      <span className="text-sm cursor-pointer flex-1">{sector}</span>
                    </label>
                  );
                })}
                {sectorFilter.size > 0 && (
                  <div className="border-t pt-2 mt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-xs"
                      onClick={() => setSectorFilter(new Set())}
                    >
                      {t('clearAll')}
                    </Button>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>

          {availableProductContexts.length > 0 && (
            <Select
              value={productContextFilter || 'all'}
              onValueChange={setProductContextFilter}
            >
              <SelectTrigger className="w-full md:w-[140px] text-sm">
                <SelectValue placeholder={t('allProducts')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('allProducts')}</SelectItem>
                {availableProductContexts.map(pc => (
                  <SelectItem key={pc} value={pc}>{pc}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Select
            value={playbookFilter ? (sequenceEventFilterId ? `${playbookFilter}::${sequenceEventFilterId}` : playbookFilter) : 'all'}
            onValueChange={(value) => {
              const parsed = parsePlaybookOptionValue(value);
              setPlaybookFilter(parsed.key);
              setSequenceEventFilterId(parsed.sequenceEventId);
            }}
          >
            <SelectTrigger className="w-full md:w-[160px] text-sm">
              <SelectValue placeholder={t('allPlaybooks')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('allPlaybooks')}</SelectItem>
              
              {mergedAvailablePlaybooks.some(p => isCustomPlaybookOption(p)) && (
                <>
                  <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                    Custom Playbooks
                  </div>
                  {mergedAvailablePlaybooks
                    .filter(p => isCustomPlaybookOption(p))
                    .map(playbook => (
                      <SelectItem key={`diy-${getPlaybookOptionValue(playbook)}`} value={getPlaybookOptionValue(playbook)}>
                        {playbook.name}
                      </SelectItem>
                    ))
                  }
                </>
              )}
              
              {mergedAvailablePlaybooks.some(p => !isCustomPlaybookOption(p)) && (
                <>
                  <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                    System Playbooks
                  </div>
                  {mergedAvailablePlaybooks
                    .filter(p => !isCustomPlaybookOption(p))
                    .map(playbook => (
                      <SelectItem key={`sys-${getPlaybookOptionValue(playbook)}`} value={getPlaybookOptionValue(playbook)}>
                        {playbook.name}
                      </SelectItem>
                    ))
                  }
                </>
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Bulk Actions Toolbar */}
        {selectedDraftIds.size > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
            <p className="text-sm font-medium text-blue-900">
              {selectedDraftIds.size} draft{selectedDraftIds.size > 1 ? 's' : ''} selected
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleBulkSchedule}
                disabled={isSending || isDeleting || isScheduling}
              >
                {isScheduling ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Scheduling...
                  </>
                ) : (
                  <>
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule
                  </>
                )}
              </Button>
              <Button 
                size="sm"
                onClick={handleBulkSend}
                disabled={isSending || isDeleting || isScheduling}
                className="bg-[#00C2C2] hover:bg-[#00A3A3]"
              >
                {isSending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Now
                  </>
                )}
              </Button>
              <Button 
                variant="destructive" 
                size="sm"
                onClick={handleBulkDeleteClick}
                disabled={isDeleting || isSending || isScheduling}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Selected
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Drafts List */}
        {sortedCampaignEntries.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Mail className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-[#5C6B7A]">{t('noDraftsFound')}</p>
              <p className="text-sm text-[#5C6B7A] mt-1">
                {t('createPlaybooksToGenerateDrafts')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="space-y-6">
              {sortedCampaignEntries.map(([campaignKey, grouped]) => {
                const accountEntries = Object.entries(grouped.accounts);
                const totalDrafts = accountEntries.reduce((sum, [, drafts]) => sum + drafts.length, 0);
                const accountCount = grouped.isAdHocScheduledGroup ? 0 : accountEntries.length;
                const statuses = new Set(accountEntries.flatMap(([, drafts]) => drafts.map((d) => d.status)));
                const statusText = grouped.isAdHocScheduledGroup
                  ? 'Scheduled'
                  : statuses.size === 1
                    ? (Array.from(statuses)[0] === 'draft_ready' ? 'Draft' : Array.from(statuses)[0])
                    : 'Mixed';

                const cardInner = (
                  <>
                    {!grouped.isAdHocScheduledGroup && (
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <h3 className="font-semibold text-[#0A1A2F]">
                        {grouped.campaign.displayName}
                      </h3>
                      <Badge variant={grouped.campaign.isEventCampaign ? 'default' : 'secondary'}>
                        {grouped.campaign.isEventCampaign ? 'Event Campaign' : 'Playbook'}
                      </Badge>
                      <Badge variant="outline">{accountCount} account{accountCount !== 1 ? 's' : ''} targeted</Badge>
                      <Badge variant="outline">Status: {statusText}</Badge>
                      <Badge variant="outline" className="ml-auto">
                        {totalDrafts} draft{totalDrafts !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                    )}

                    <div className="space-y-3">
                      {accountEntries.map(([accountName, accountDrafts]) => (
                        <div key={`${campaignKey}-${accountName}`} className="space-y-3">
                          {!grouped.isAdHocScheduledGroup && (
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-[#5C6B7A]" />
                            <h4 className="font-medium text-[#0A1A2F]">{accountName}</h4>
                            <Badge variant="outline" className="ml-auto">
                              {accountDrafts.length} draft{accountDrafts.length !== 1 ? 's' : ''}
                            </Badge>
                            {accountDrafts.length > 1 && (
                              <Checkbox
                                checked={accountDrafts.every(d => selectedDraftIds.has(d.stepId || d.id))}
                                onCheckedChange={() => {
                                  const allSelected = accountDrafts.every(d => selectedDraftIds.has(d.stepId || d.id));
                                  const accountIds = accountDrafts.map(d => d.stepId || d.id);
                                  
                                  setSelectedDraftIds(prev => {
                                    const next = new Set(prev);
                                    if (allSelected) {
                                      accountIds.forEach(id => next.delete(id));
                                    } else {
                                      accountIds.forEach(id => next.add(id));
                                    }
                                    return next;
                                  });
                                }}
                              />
                            )}
                          </div>
                          )}
                          {accountDrafts.map((draft) => {
                        const stageNo = draft.stage || draft.stage_no;
                        const isAdHocScheduled = isAdHocScheduledOutboxDraft(draft);
                        const dateLine = formatOutboxDateLine(draft);
                        const stepStatus = resolveOutboxDisplayStatus(draft);
                        const statusBadge = getOutboxStatusBadge(stepStatus);
                        const statusSubtext = getOutboxStatusSubtext(stepStatus, draft);
                        const persona = getPersonaForDraft(draft);
                        const isFormerClient = Boolean(
                          persona?.worked_for_customer ??
                          draft?.worked_for_customer ??
                          draft?.person?.worked_for_customer
                        );
                        const personaFitScore = getPersonaFitScore(draft, persona);
                        const fit = personaFitScore !== null ? getPersonaLabel(personaFitScore) : null;
                        const socialPresenceScore = isLinkedInOutboxChannel(draft)
                          ? getSocialPresenceScore(draft, persona)
                          : null;
                        const draftId = draft.stepId || draft.id;
                        const isSelected = selectedDraftIds.has(draftId);
                        const isSent = draft.status === 'sent';
                        const isGenerating = isOutboxGeneratingStatus(stepStatus);

                        return (
                          <div
                            key={`step-${draftId}`}
                            className={`flex flex-col md:flex-row p-2 md:p-4 border rounded-lg transition-colors gap-2 md:gap-3 ${
                              isSelected ? 'border-blue-300 bg-blue-50' : 'hover:bg-gray-50'
                            } ${
                              isGenerating ? 'opacity-70' : ''
                            }`}
                          >
                            <div className="flex items-start gap-2 md:gap-3 flex-1 min-w-0">
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => handleToggleSelect(draftId)}
                                disabled={isSent}
                                className="mt-0.5 md:mt-1"
                              />
                              
                              {isEmailOutboxChannel(draft) ? (
                                <Mail className="w-3.5 h-3.5 md:w-5 md:h-5 text-[#00C2C2] mt-0.5 md:mt-1 flex-shrink-0" />
                              ) : isLinkedInOutboxChannel(draft) ? (
                                <Linkedin className="w-3.5 h-3.5 md:w-5 md:h-5 text-[#0077B5] mt-0.5 md:mt-1 flex-shrink-0" />
                              ) : isCallOutboxChannel(draft) ? (
                                <Phone className="w-3.5 h-3.5 md:w-5 md:h-5 text-green-600 mt-0.5 md:mt-1 flex-shrink-0" />
                              ) : (
                                <Mail className="w-3.5 h-3.5 md:w-5 md:h-5 text-[#00C2C2] mt-0.5 md:mt-1 flex-shrink-0" />
                              )}
                              <div 
                                className="flex-1 min-w-0 cursor-pointer"
                                onClick={() => setSelectedDraft(draft)}
                              >
                                <div className="flex flex-wrap items-center gap-1 md:gap-1.5 mb-0.5 md:mb-1">
                                  {(() => {
                                    const displayName =
                                      draft.person?.name || draft.person_name || 'Unknown';
                                    const profileUrl = getPersonProfileUrlFromDraft(draft);
                                    const nameClass =
                                      'font-medium text-sm md:text-base text-[#0A1A2F] break-words leading-tight';
                                    return (
                                      <p className={nameClass}>
                                        {profileUrl ? (
                                          <Link
                                            to={profileUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="hover:text-[#00A3A3] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2C2] rounded-sm"
                                          >
                                            {displayName}
                                          </Link>
                                        ) : (
                                          displayName
                                        )}
                                      </p>
                                    );
                                  })()}
                                  {stageNo && (
                                    <Badge variant="secondary" className="text-[10px] md:text-xs whitespace-nowrap px-1 md:px-2 py-0.5">
                                      Touch {stageNo}
                                    </Badge>
                                  )}
                                  <LinkedInSubtypeBadge
                                    source={draft}
                                    debugContext="outbox-list"
                                  />
                                  {draft.product_context && (
                                    <Badge variant="outline" className="text-[10px] md:text-xs whitespace-nowrap px-1 md:px-2 py-0.5 bg-[#00C2C2]/10 text-[#00A3A3] border-[#00C2C2]/30">
                                      {draft.product_context}
                                    </Badge>
                                  )}
                                  {isFormerClient && (
                                    <Badge variant="outline" className="text-[10px] md:text-xs whitespace-nowrap px-1 md:px-2 py-0.5 bg-[#10B981]/10 text-[#047857] border-[#10B981]/30">
                                      {t('formerClient')}
                                    </Badge>
                                  )}
                                  {fit && (
                                    <Badge className={`${fit.color} text-[10px] md:text-xs whitespace-nowrap px-1 md:px-2 py-0.5`}>
                                      {fit.label} ({personaFitScore})
                                    </Badge>
                                  )}
                                  {socialPresenceScore !== null && (
                                    <Badge variant="outline" className="text-[10px] md:text-xs whitespace-nowrap px-1 md:px-2 py-0.5 bg-[#0077B5]/10 text-[#005885] border-[#0077B5]/30">
                                      Social Presence ({socialPresenceScore})
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-xs md:text-sm text-[#5C6B7A] line-clamp-2 md:line-clamp-3 break-words">
                                  {isAdHocScheduled ? (
                                    <>
                                      {(draft.account?.name || draft.account_name) && (
                                        <span>{draft.account?.name || draft.account_name}</span>
                                      )}
                                      {dateLine && (
                                        <>
                                          {(draft.account?.name || draft.account_name) ? ' · ' : ''}
                                          <span className={dateLine.className}>
                                            {dateLine.prefix} {dateLine.dateText}
                                          </span>
                                        </>
                                      )}
                                      {draft.subject && (
                                        <>
                                          {' · Subject: '}
                                          <span className="text-[#0A1A2F]">
                                            {isGenerating ? 'Generating...' : draft.subject}
                                          </span>
                                        </>
                                      )}
                                    </>
                                  ) : (
                                    getOutboxDraftHeadline(draft, { isGenerating })
                                  )}
                                </p>
                                {dateLine && !isAdHocScheduled && (
                                  <div
                                    className={`flex items-center gap-1 mt-0.5 md:mt-1 text-[10px] md:text-xs ${dateLine.className}`}
                                  >
                                    <Calendar className="w-2.5 h-2.5 md:w-3 md:h-3 shrink-0" />
                                    <span>{dateLine.prefix}</span>
                                    <span className={dateLine.italic ? 'italic' : ''}>
                                      {dateLine.dateText}
                                    </span>
                                  </div>
                                )}
                                {(() => {
                                  const lastSent = getLastSentInfo(draft);
                                  return lastSent ? (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <div className="flex flex-wrap items-center gap-1 mt-0.5 md:mt-1 text-[10px] md:text-xs text-[#5C6B7A] cursor-default">
                                            <span className="text-[#00C2C2]">Last playbook touch:</span>
                                            <span className="font-medium">{lastSent.channel}</span>
                                            <span>•</span>
                                            <span>{lastSent.date}</span>
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p className="text-xs">{lastSent.fullDate}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  ) : null;
                                })()}
                                {(() => {
                                  const playbookEmailRaw =
                                    draft?.last_playbook_email_sent_at ?? draft?.lastPlaybookEmailSentAt;
                                  const playbookTouchRaw = draft?.last_sent_at ?? draft?.lastSentAt;
                                  if (!playbookEmailRaw) return null;
                                  const emailAt = toValidDate(playbookEmailRaw);
                                  if (!emailAt) return null;
                                  const touchAt = toValidDate(playbookTouchRaw);
                                  const showPlaybookEmailLine =
                                    !touchAt || emailAt.getTime() !== touchAt.getTime();
                                  if (!showPlaybookEmailLine) return null;
                                  const channelLabel = getChannelDisplayLabel({
                                    channel:
                                      draft?.last_playbook_email_channel ??
                                      draft?.lastPlaybookEmailChannel ??
                                      'email',
                                  });
                                  return (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <div className="flex flex-wrap items-center gap-1 mt-0.5 md:mt-1 text-[10px] md:text-xs text-[#5C6B7A] cursor-default">
                                            <span className="text-[#00C2C2]">Last playbook email:</span>
                                            <span className="font-medium">{channelLabel}</span>
                                            <span>•</span>
                                            <span>{formatRelativeDateLabel(emailAt)}</span>
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p className="text-xs">
                                            {emailAt.toLocaleDateString([], {
                                              month: 'short',
                                              day: 'numeric',
                                              year: 'numeric',
                                              hour: 'numeric',
                                              minute: '2-digit',
                                            })}
                                          </p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  );
                                })()}
                                {(() => {
                                  const adHocInfo = getLastAdHocInfoForDraft(draft);
                                  return adHocInfo ? (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <div className="flex flex-wrap items-center gap-1 mt-0.5 md:mt-1 text-[10px] md:text-xs text-[#6D28D9] cursor-default">
                                            <span className="font-semibold text-[#7C3AED]">Last ad-hoc sent:</span>
                                            {adHocInfo.channel ? (
                                              <>
                                                <span className="font-medium">{adHocInfo.channel}</span>
                                                <span>•</span>
                                              </>
                                            ) : null}
                                            <span className="font-medium">{adHocInfo.date}</span>
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p className="text-xs">{adHocInfo.fullDate}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  ) : null;
                                })()}
                                {/* [2026-07-01] Last PLAYBOOK touch sent — mirrors the ad-hoc
                                    line above but for sequence-driven sends. Fixes the
                                    Zalizah case where 5 delivered playbook emails weren't
                                    reflected on the Outbox row. Emerald color to distinguish
                                    from the purple ad-hoc line. */}
                                {(() => {
                                  const pbInfo = getLastPlaybookInfoForDraft(draft);
                                  return pbInfo ? (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <div className="flex flex-wrap items-center gap-1 mt-0.5 md:mt-1 text-[10px] md:text-xs text-emerald-800 cursor-default">
                                            <span className="font-semibold text-emerald-700">Last playbook touch sent:</span>
                                            {pbInfo.stage_no != null ? (
                                              <>
                                                <span className="font-medium">T{pbInfo.stage_no}</span>
                                                <span>•</span>
                                              </>
                                            ) : null}
                                            {pbInfo.channel ? (
                                              <>
                                                <span className="font-medium">{pbInfo.channel}</span>
                                                <span>•</span>
                                              </>
                                            ) : null}
                                            <span className="font-medium">{pbInfo.date}</span>
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p className="text-xs">{pbInfo.fullDate}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  ) : null;
                                })()}
                                {(() => {
                                  const scheduledInfo = getScheduledInfoForDraft(draft);
                                  if (!scheduledInfo?.next) return null;
                                  const { next, count, items } = scheduledInfo;
                                  return (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <div className="flex flex-wrap items-center gap-1 mt-0.5 md:mt-1 text-[10px] md:text-xs text-blue-800 cursor-default">
                                            <span className="font-semibold text-blue-700">
                                              {count > 1 ? `Also scheduled (${count}):` : 'Also scheduled:'}
                                            </span>
                                            <span className="font-medium">{next.channel}</span>
                                            <span>•</span>
                                            <span className="font-medium">{next.date}</span>
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-xs">
                                          <div className="space-y-1.5">
                                            {items.map((item) => (
                                              <p key={item.stepId || `${item.date}-${item.channel}`} className="text-xs">
                                                <span className="font-medium">{item.channel}</span>
                                                {' · '}
                                                {item.fullDate}
                                                {item.subject ? (
                                                  <>
                                                    <br />
                                                    <span className="text-gray-500">{item.subject}</span>
                                                  </>
                                                ) : null}
                                              </p>
                                            ))}
                                          </div>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  );
                                })()}
                              </div>
                            </div>

                            <div className="flex flex-col items-end justify-center gap-0.5 flex-shrink-0 ml-8 md:ml-0">
                              {statusBadge && (
                                <Badge
                                  className={`${statusBadge.className} text-[10px] md:text-xs whitespace-nowrap px-1.5 py-0.5 border`}
                                >
                                  {statusBadge.showSpinner && (
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin inline" />
                                  )}
                                  {statusBadge.label}
                                </Badge>
                              )}
                              {statusSubtext && (
                                <p className="text-[10px] md:text-xs text-gray-500 text-right max-w-[10rem] leading-tight">
                                  {statusSubtext}
                                </p>
                              )}
                              {stepStatus === 'draft_ready' && !isAdHocScheduled && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => handleMarkAsDone(draft, e)}
                                  className="text-[10px] md:text-xs whitespace-nowrap h-7 md:h-8 px-2 md:px-3 mt-1"
                                >
                                  <CheckCircle2 className="w-2.5 h-2.5 md:w-3 md:h-3 mr-0.5 md:mr-1" />
                                  Done
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                          })}
                        </div>
                      ))}
                    </div>
                  </>
                );

                return (
                  <Card key={campaignKey}>
                    <CardContent className="pt-6">
                      {grouped.isAdHocScheduledGroup ? (
                        <details open className="group">
                          <summary className="list-none cursor-pointer select-none [&::-webkit-details-marker]:hidden outline-none">
                            <div className="flex items-center gap-2 mb-4 flex-wrap">
                              <ChevronDown className="w-4 h-4 text-gray-500 shrink-0 group-open:rotate-180 transition-transform" />
                              <h3 className="font-semibold text-[#0A1A2F]">
                                {grouped.campaign.displayName} · {totalDrafts} pending
                              </h3>
                              <Badge className="bg-blue-100 text-blue-800 border-blue-200">Scheduled</Badge>
                              <Badge variant="outline">Status: {statusText}</Badge>
                            </div>
                          </summary>
                          {cardInner}
                        </details>
                      ) : (
                        cardInner
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Pagination Controls */}
            {pagination.total_count > ITEMS_PER_PAGE && (
              <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-4 rounded-lg">
                {/* Mobile: Simple prev/next */}
                <div className="flex flex-1 justify-between sm:hidden">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    {t('previous')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!pagination.has_more}
                  >
                    {t('next')}
                  </Button>
                </div>
                
                {/* Desktop: Full pagination */}
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing{' '}
                      <span className="font-medium">
                        {((currentPage - 1) * ITEMS_PER_PAGE) + 1}
                      </span>
                      {' '}-{' '}
                      <span className="font-medium">
                        {Math.min(currentPage * ITEMS_PER_PAGE, pagination.total_count)}
                      </span>
                      {' '}of{' '}
                      <span className="font-medium">{pagination.total_count}</span>
                      {' '}results
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      {t('previous')}
                    </Button>
                    
                    {/* Page numbers */}
                    <div className="flex items-center gap-1">
                      {generatePageNumbers(currentPage, pagination.total_pages).map((pageNum, idx) => (
                        pageNum === '...' ? (
                          <span key={`ellipsis-${idx}`} className="px-2 text-gray-500">...</span>
                        ) : (
                          <Button
                            key={pageNum}
                            variant={pageNum === currentPage ? 'default' : 'outline'}
                            size="sm"
                            className="w-8 h-8 p-0"
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </Button>
                        )
                      ))}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!pagination.has_more}
                    >
                      {t('next')}
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Drawer */}
      {selectedDraft && (
        <OutboxDrawer
          draft={selectedDraft}
          peerDrafts={drafts}
          onClose={() => setSelectedDraft(null)}
          onSent={handleDraftSent}
          tenantId={tenantId}
          userId={user?.id}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteTarget?.type === 'single' ? t('cancelDraft') : t('cancelDrafts')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.type === 'single' 
                ? `This will cancel the draft message to ${deleteTarget.draft?.person?.name}. This action cannot be undone.`
                : `This will cancel ${deleteTarget?.count} draft messages. This action cannot be undone.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('deleting')}
                </>
              ) : deleteTarget?.type === 'single' ? (
                t('deleteDraft')
              ) : (
                `Delete ${deleteTarget?.count} Drafts`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Resend Blocked Dialog */}
      <AlertDialog
        open={!!resendBlocked}
        onOpenChange={(open) => {
          if (!open && !isHandlingResendBlocked) {
            setResendBlocked(null);
            setResendBlockedSecondsLeft(0);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            {(() => {
              const cooldownH = resendBlocked?.cooldownWindowHours;
              const lastChannel = resendBlocked?.cooldownLastChannel;
              const name = resendBlocked?.step?.person?.name || resendBlocked?.recipientEmail || 'this contact';
              const lastSentAt = resendBlocked?.lastSentAt;
              let agoLabel = '';
              if (lastSentAt) {
                const ms = Date.now() - new Date(lastSentAt).getTime();
                const hrs = Math.round(ms / 3600000);
                if (hrs < 1) agoLabel = 'less than an hour ago';
                else if (hrs < 24) agoLabel = `~${hrs}h ago`;
                else if (hrs < 48) agoLabel = 'yesterday';
                else agoLabel = `${Math.round(hrs / 24)} days ago`;
              }
              if (cooldownH && cooldownH > 1) {
                return (
                  <>
                    <AlertDialogTitle>Already touched this contact recently</AlertDialogTitle>
                    <AlertDialogDescription>
                      You sent a <span className="font-medium">{lastChannel || 'message'}</span> to{' '}
                      <span className="font-medium">{name}</span> {agoLabel || 'in the last day or two'}.
                      <br />
                      Two touches in {cooldownH < 48 ? 'one day' : 'a couple of days'} can read as pushy. Schedule it,
                      or send anyway if it&apos;s a genuine follow-up.
                    </AlertDialogDescription>
                  </>
                );
              }
              return (
                <>
                  <AlertDialogTitle>Already sent recently</AlertDialogTitle>
                  <AlertDialogDescription>
                    You sent a message to <span className="font-medium">{resendBlocked?.recipientEmail || 'this recipient'}</span>{' '}
                    {Number.isFinite(resendBlocked?.secondsAgo) ? `${resendBlocked.secondsAgo}s ago.` : 'recently.'}
                    <br />
                    Sending another now might look spammy.
                  </AlertDialogDescription>
                </>
              );
            })()}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isHandlingResendBlocked}>Cancel</AlertDialogCancel>
            <Button
              variant="outline"
              disabled={isHandlingResendBlocked || resendBlockedSecondsLeft > 0}
              onClick={handleResendBlockedRetryNow}
            >
              {resendBlockedSecondsLeft > 0
                ? `Wait ${resendBlockedSecondsLeft}s`
                : (resendBlocked?.cooldownWindowHours > 1 ? 'Send anyway' : 'Wait and retry')}
            </Button>
            <AlertDialogAction
              onClick={handleResendBlockedSchedule}
              disabled={isHandlingResendBlocked}
              className="bg-[#00C2C2] hover:bg-[#00A3A3]"
            >
              Schedule instead
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}